/**
 * Divorce Application Detail Page
 * Displays detailed information for a selected divorce certificate application
 */

import React, { useEffect, useState } from "react";
import { serviceChargeService } from "../services/api/serviceCharge.service";
import type { ServiceCharge } from "../types/serviceCharge.types";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { usePageTitle } from "../contexts/PageTitleContext";
import { BASE_URL } from "../config/api";
import { certificateService } from "../services/api/certificate.service";
import { applicantService } from "../services/api/applicant.service";
import { paymentService } from "../services/api/payment.service";
import { lawyerService } from "../services/api/lawyer.service";
import type { Lawyer, CreateLawyerDto } from "../types/lawyer.types";
import { LawyerCategory } from "../types/lawyer.types";
import { settingsService } from "../services/api/settings.service";
import { notificationService } from "../services/api/notification.service";
import { workflowService } from "../services/api/workflow.service";
import type { QueryWorkflowActionDto, Workflow } from "../types/workflow.types";
import type {
  CertificateApplication,
  UpdateCertificateApplicationDto,
  County,
  Nationality,
} from "../types/certificate.types";
import type {
  Applicant,
  CreateApplicantDto,
  UpdateApplicantDto,
} from "../types/applicant.types";
import type { Attachment } from "../types/attachment.types";
import { ApplicationCategory, CountyEnum } from "../types/certificate.types";
import { UserStatusId } from "../types/user.types";
import BusyOverlay from "../components/common/BusyOverlay";
import Sidebar from "../components/Sidebar";
import {
  ApplicationPermissionId,
  HasPermission,
  ModuleId,
} from "../utils/permissions";
import Topbar from "../components/Topbar";
import Footer from "../components/Footer";
import WorkflowStages from "../components/common/WorkflowStages";
import {
  showSuccessToast,
  showErrorToast,
  showDeleteConfirm,
  showConfirm,
} from "../utils/toast";
import {
  GENERIC_ERROR_MESSAGE,
  REQUIRED_FIELD_MESSAGE,
} from "../utils/constants";
import { sanitizePhoneNumberInput, validatePhoneNumber } from "../utils/phone";
import "../styles/pages/UserDetail.css";
import "../styles/pages/Users.css";
import { CardPayment } from "./CardPayment";
import { ManualPayment } from "./ManualPayment";
import { DocumentScan } from "./DocumentScan";
import { SignatureExtraction } from "./SignatureExtraction";
import { DivorceCert } from "../components/DivorceCert";
import History from "../components/common/History";
import QueryRemarks from "../components/common/QueryRemarks";
import QueryApplicationForm from "../components/common/QueryApplicationForm";
import { apiRequest } from "../api/apiClient";
import { getFullPath } from "../utils/getFullPath";
import { generatePassportPhoto } from "../utils/image-processing/passportPhotoHelper";

// Default nationality constant
const DefaultNationality = "Liberian";
const BasicRole = 3;
const DIVORCE_SUBMISSION_REQUIREMENT_KEYS = [
  "plaintiffExists",
  "plaintiffLawyerExists",
  "defendantExists",
  "defendantLawyerExists",
  "paymentMade",
] as const;

interface DivorceApplicantResponse {
  id?: number;
  certificateApplicationId: number;
  userId?: number;
  uid?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth?: string;
  nationalIdNumber?: string;
  email?: string;
  primaryPhone?: string;
  secondaryPhone?: string;
  applicantType?: number | string;
  applicantTypeDisplay?: string;
  genderId?: number;
  nationalityId?: number;
  nationalityName?: string;
  birthCountyId?: number;
  birthCountyName?: string;
  birthTown?: string;
  currentAddress?: string;
  photoUrl?: string;
  profileImagePath?: string;
  previousName?: string;
  occupation?: string;
  placeOfWork?: string;
  countyOfOriginId?: number;
  wasPreviouslyMarried?: boolean;
  previousMarriageDate?: string;
  previousSpouseName?: string;
  fatherName?: string;
  motherName?: string;
  createdBy?: number;
  createdByName?: string;
  createdAt?: string;
  updatedBy?: number;
  updatedByName?: string;
  updatedAt?: string;
}

// Calculate max date for 18+ age requirement (18 years before current date)
const getMaxDateFor18Plus = (): string => {
  const today = new Date();
  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  );
  return eighteenYearsAgo.toISOString().split("T")[0];
};

export const DivorceApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Determine back navigation path based on current URL
  const getBackPath = () => {
    if (location.pathname.includes("/approval/")) {
      return "/divorce/approval";
    }
    if (location.pathname.includes("/print/")) {
      return "/divorce/print";
    }
    if (location.pathname.includes("/finalize/")) {
      return "/divorce/apply";
    }
    return "/divorce/apply";
  };

  // Check if we're on the print page
  const isPrintPage = location.pathname.includes("/print/");

  // Check if we're on the finalize page
  const isFinalizePage = location.pathname.includes("/finalize/");

  const [application, setApplication] = useState<CertificateApplication | null>(
    null,
  );
  const [counties, setCounties] = useState<County[]>([]);
  const [nationalities, setNationalities] = useState<Nationality[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [ministryName, setMinistryName] = useState<string>(
    "Ministry of Internal Affairs",
  );
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");

  // Workflow approval state
  const [isAtMyApprovalLevel, setIsAtMyApprovalLevel] = useState(false);
  const [currentStageOrder, setCurrentStageOrder] = useState(0);

  // Signed certificate state
  const [signedCertificate, setSignedCertificate] = useState<any>(null);
  const [signedCertLoading, setSignedCertLoading] = useState(false);

  // Signature extraction modal state
  const [showSignatureExtractionModal, setShowSignatureExtractionModal] =
    useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showQueryRemarks, setShowQueryRemarks] = useState(false);
  const [hasQueryRemarks, setHasQueryRemarks] = useState(false);
  const [showQueryForm, setShowQueryForm] = useState(false);

  // Accordion state
  const [plaintiffAccordionOpen, setPlaintiffAccordionOpen] = useState(false);
  const [defendantAccordionOpen, setDefendantAccordionOpen] = useState(false);
  const [defendantLawyerAccordionOpen, setDefendantLawyerAccordionOpen] =
    useState(false);
  const [plaintiffLawyerAccordionOpen, setPlaintiffLawyerAccordionOpen] =
    useState(false);
  const [attachmentsAccordionOpen, setAttachmentsAccordionOpen] =
    useState(false);

  // Payment dropdown state
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
  const [hasExistingPayment, setHasExistingPayment] = useState(false);

  // Submission readiness state
  const [isReadyForSubmission, setIsReadyForSubmission] = useState(false);
  const [submissionRequirements, setSubmissionRequirements] = useState<
    Record<string, boolean>
  >({});

  // Service charge state for edit modal (for Marriage applications)
  const [editServiceCharge, setEditServiceCharge] =
    useState<ServiceCharge | null>(null);

  // Manual payment modal state
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
  const [showCardPaymentModal, setShowCardPaymentModal] = useState(false);

  // Document scan modal state
  const [showDocumentScanModal, setShowDocumentScanModal] = useState(false);

  // Attachments state
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [viewingAttachmentId, setViewingAttachmentId] = useState<number | null>(
    null,
  );

  // Plaintiff applicant state
  const [plaintiffApplicant, setPlaintiffApplicant] =
    useState<Applicant | null>(null);
  const [plaintiffFormData, setPlaintiffFormData] =
    useState<CreateApplicantDto>({
      userId: undefined,
      firstName: "",
      middleName: "",
      lastName: "",
      previousName: "",
      genderId: 0,
      dateOfBirth: "",
      nationalIdNumber: "",
      email: "",
      primaryPhone: "",
      secondaryPhone: "",
      certificateApplicationId: 0,
      applicantType: "Plaintiff",
      countyOfOriginId: 0,
      nationalityId: 0,
      birthCountyId: 0,
      birthTown: "",
      currentAddress: "",
      occupation: "",
      placeOfWork: "",
      wasPreviouslyMarried: false,
      previousMarriageDate: "",
      previousSpouseName: "",
      fatherName: "",
      motherName: "",
    });
  const [plaintiffLoading, setPlaintiffLoading] = useState(false);
  const [plaintiffError, setPlaintiffError] = useState("");
  const [plaintiffValidationErrors, setPlaintiffValidationErrors] = useState<
    Record<string, string>
  >({});
  const [photoProcessing, setPhotoProcessing] = useState(false);
  const [plaintiffPhotoFile, setPlaintiffPhotoFile] = useState<File | null>(
    null,
  );
  const [plaintiffPhotoPreview, setPlaintiffPhotoPreview] = useState<
    string | null
  >(null);

  // Defendant applicant state
  const [defendantApplicant, setDefendantApplicant] =
    useState<Applicant | null>(null);
  const [defendantFormData, setDefendantFormData] =
    useState<CreateApplicantDto>({
      userId: undefined,
      firstName: "",
      middleName: "",
      lastName: "",
      previousName: "",
      genderId: 0,
      dateOfBirth: "",
      nationalIdNumber: "",
      email: "",
      primaryPhone: "",
      secondaryPhone: "",
      certificateApplicationId: 0,
      applicantType: "Defendant",
      countyOfOriginId: 0,
      nationalityId: 0,
      birthCountyId: 0,
      birthTown: "",
      currentAddress: "",
      occupation: "",
      placeOfWork: "",
      wasPreviouslyMarried: false,
      previousMarriageDate: "",
      previousSpouseName: "",
      fatherName: "",
      motherName: "",
    });
  const [defendantLoading, setDefendantLoading] = useState(false);
  const [defendantError, setDefendantError] = useState("");
  const [defendantValidationErrors, setDefendantValidationErrors] = useState<
    Record<string, string>
  >({});
  const [defendantPhotoFile, setDefendantPhotoFile] = useState<File | null>(
    null,
  );
  const [defendantPhotoPreview, setDefendantPhotoPreview] = useState<
    string | null
  >(null);

  // Defendant Lawyer state (Divorce)
  const [defendantLawyer, setDefendantLawyer] = useState<Lawyer | null>(null);
  const [defendantLawyerFormData, setDefendantLawyerFormData] =
    useState<CreateLawyerDto>({
      certificateApplicationId: 0,
      name: "",
      primaryPhone: "",
      secondaryPhone: "",
      email: "",
      address: "",
      category: LawyerCategory.Defendant,
    });
  const [defendantLawyerLoading, setDefendantLawyerLoading] = useState(false);
  const [defendantLawyerError, setDefendantLawyerError] = useState("");
  const [defendantLawyerValidationErrors, setDefendantLawyerValidationErrors] =
    useState<Record<string, string>>({});

  // Plaintiff Lawyer state (Divorce)
  const [plaintiffLawyer, setPlaintiffLawyer] = useState<Lawyer | null>(null);
  const [plaintiffLawyerFormData, setPlaintiffLawyerFormData] =
    useState<CreateLawyerDto>({
      certificateApplicationId: 0,
      name: "",
      primaryPhone: "",
      secondaryPhone: "",
      email: "",
      address: "",
      category: LawyerCategory.Plaintiff,
    });
  const [plaintiffLawyerLoading, setPlaintiffLawyerLoading] = useState(false);
  const [plaintiffLawyerError, setPlaintiffLawyerError] = useState("");
  const [plaintiffLawyerValidationErrors, setPlaintiffLawyerValidationErrors] =
    useState<Record<string, string>>({});

  const canShowMakePayment = Boolean(
    application?.id &&
    plaintiffApplicant &&
    defendantApplicant &&
    plaintiffLawyer &&
    defendantLawyer,
  );

  const canShowSubmitAction = Boolean(
    application?.id &&
    plaintiffApplicant &&
    defendantApplicant &&
    plaintiffLawyer &&
    defendantLawyer &&
    hasExistingPayment,
  );

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] =
    useState<UpdateCertificateApplicationDto>({});
  const [editLoading, setEditLoading] = useState(false);
  const [isRoleCountyLinked, setIsRoleCountyLinked] = useState(false);
  const [linkedCountyId, setLinkedCountyId] = useState<number | null>(null);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle("Application Detail", "View application information");
    fetchData();
    fetchCounties();
    fetchNationalities();
    fetchSettings();
    fetchWorkflows();
    if (id) {
      fetchApplicants();
      fetchLawyers();
      fetchAttachments();
      if (isFinalizePage) {
        fetchSignedCertificate();
      }
    }
  }, [id, setPageTitle]);

  const checkRoleCountyLink = async () => {
    try {
      const response = await apiRequest("/RoleCounties/is-linked", {
        method: "GET",
      });

      if (!response.ok) {
        setIsRoleCountyLinked(false);
        setLinkedCountyId(null);
        return;
      }

      const payload = (await response.json().catch(() => null)) as {
        countyId?: number | null;
        data?: { countyId?: number | null };
      } | null;

      const source = payload?.data ?? payload;
      const countyCandidate = Number(source?.countyId);

      if (Number.isFinite(countyCandidate) && countyCandidate > 0) {
        setIsRoleCountyLinked(true);
        setLinkedCountyId(countyCandidate);
        return;
      }

      setIsRoleCountyLinked(false);
      setLinkedCountyId(null);
    } catch {
      setIsRoleCountyLinked(false);
      setLinkedCountyId(null);
    }
  };

  useEffect(() => {
    checkRoleCountyLink();
  }, []);

  useEffect(() => {
    const loadQueryRemarks = async () => {
      if (!application?.id) {
        setHasQueryRemarks(false);
        return;
      }

      try {
        const remarks = await workflowService.getRequeryRemarks(
          "CertificateApplications",
          application.id,
        );
        setHasQueryRemarks(remarks.length > 0);
      } catch {
        setHasQueryRemarks(false);
      }
    };

    loadQueryRemarks();
  }, [application?.id]);

  // Auto-select workflow based on processAtHeadquarters
  useEffect(() => {
    if (
      workflows.length > 0 &&
      editFormData.processAtHeadquarters !== undefined
    ) {
      const selectedWorkflow = editFormData.processAtHeadquarters
        ? workflows.find((w) => w.name.toLowerCase().includes("headquarters"))
        : workflows.find((w) => w.name.toLowerCase().includes("county"));

      if (selectedWorkflow && selectedWorkflow.id !== editFormData.workflowId) {
        setEditFormData((prev) => ({
          ...prev,
          workflowId: selectedWorkflow.id,
        }));
      }
    }
  }, [workflows, editFormData.processAtHeadquarters]);

  // Close payment dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        paymentDropdownOpen &&
        !target.closest(".user-detail-action-btn--warning")
      ) {
        setPaymentDropdownOpen(false);
      }
    };

    if (paymentDropdownOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [paymentDropdownOpen]);

  // Fetch Currency and ServiceCharge from /ServiceCharges/category/1 for edit modal
  useEffect(() => {
    if (showEditModal) {
      const fetchPrice = async () => {
        try {
          const weddingPrice =
            await serviceChargeService.getServiceChargeByCategoryId(1);
          setEditServiceCharge(weddingPrice);
        } catch {
          setEditServiceCharge(null);
        }
      };
      fetchPrice();
    } else {
      setEditServiceCharge(null);
    }
  }, [showEditModal]);

  const fetchData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError("");
      const data = await certificateService.getCertificateApplicationById(
        parseInt(id, 10),
      );
      setApplication(data);

      // Check if workflow is at user's approval level and get current stage
      if (data.workflowId) {
        try {
          const [atMyLevel, currentStage] = await Promise.all([
            workflowService.isAtMyApprovalLevel(data.workflowId, data.id!),
            workflowService.getCurrentStageRole(data.workflowId, data.id!),
          ]);
          setIsAtMyApprovalLevel(atMyLevel);
          setCurrentStageOrder(currentStage.stageOrder);
        } catch (err) {
          console.error("Error checking approval level:", err);
          setIsAtMyApprovalLevel(false);
          setCurrentStageOrder(0);
        }
      } else {
        setIsAtMyApprovalLevel(false);
        setCurrentStageOrder(0);
      }

      // Check for existing payments
      await checkExistingPayments(parseInt(id, 10));

      // Check if ready for submission
      await checkReadyForSubmission(parseInt(id, 10));
    } catch (err: any) {
      console.error("Error fetching application:", err);
      setError(err?.message || "Failed to load application");
    } finally {
      setLoading(false);
    }
  };

  const checkExistingPayments = async (applicationId: number) => {
    try {
      const payments =
        await paymentService.getPaymentsByApplicationId(applicationId);
      setHasExistingPayment(payments && payments.length > 0);
    } catch (err: any) {
      console.error("Error checking payments:", err);
      // Don't show error to user, just assume no payments
      setHasExistingPayment(false);
    }
  };

  const checkReadyForSubmission = async (applicationId: number) => {
    try {
      const readinessChecks = await certificateService.checkReadyForSubmission(
        applicationId,
        ModuleId.Divorces,
      );

      const normalizedRequirements = DIVORCE_SUBMISSION_REQUIREMENT_KEYS.reduce<
        Record<string, boolean>
      >((acc, key) => {
        acc[key] = readinessChecks?.[key] === true;
        return acc;
      }, {});

      // Store only the expected divorce readiness checks.
      setSubmissionRequirements(normalizedRequirements);

      // Divorce is ready only when all required keys are strictly true.
      const allReady = DIVORCE_SUBMISSION_REQUIREMENT_KEYS.every(
        (key) => readinessChecks?.[key] === true,
      );
      setIsReadyForSubmission(allReady);
    } catch (err: any) {
      console.error("Error checking submission readiness:", err);
      // Don't show error to user, just assume not ready
      setIsReadyForSubmission(false);
      setSubmissionRequirements({});
    }
  };

  const fetchCounties = async () => {
    try {
      const data = await certificateService.getCounties();
      setCounties(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching counties:", err);
    }
  };

  const fetchNationalities = async () => {
    try {
      const data = await certificateService.getNationalities();
      setNationalities(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching nationalities:", err);
    }
  };

  const fetchSettings = async () => {
    try {
      const settings = await settingsService.getSettings();
      const ministrySetting = settings.find(
        (s) => s.key === "ministryName" && s.category === "general",
      );
      if (ministrySetting?.value) {
        setMinistryName(ministrySetting.value);
      }
    } catch (err: any) {
      console.error("Error fetching settings:", err);
      // Keep default value on error
    }
  };

  const fetchWorkflows = async () => {
    try {
      const data = await workflowService.getWorkflowsByModule(
        ModuleId.Divorces,
      );
      setWorkflows(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching workflows:", err);
    }
  };

  const fetchAttachments = async () => {
    if (!id) return;

    try {
      setAttachmentsLoading(true);
      const response = await apiRequest(`/Attachments/by-application/${id}`, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch attachments");
      }

      const data = await response.json();
      const attachmentsArray = Array.isArray(data) ? data : [];
      setAttachments(attachmentsArray);
    } catch (err: any) {
      console.error("Error fetching attachments:", err);
      // Don't show error toast, just log it
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const fetchSignedCertificate = async () => {
    if (!id) return;

    try {
      setSignedCertLoading(true);
      const response = await apiRequest(
        `/SignedCertificates/by-application/${id}`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        // Don't throw error, just set null if not found
        setSignedCertificate(null);
        return;
      }

      const data = await response.json();
      // API returns an array, get the first item
      const certArray = Array.isArray(data) ? data : [];
      setSignedCertificate(certArray.length > 0 ? certArray[0] : null);
    } catch (err: any) {
      console.error("Error fetching signed certificate:", err);
      setSignedCertificate(null);
    } finally {
      setSignedCertLoading(false);
    }
  };

  const handleDeleteAttachment = async (attachment: Attachment) => {
    const confirmed = await showDeleteConfirm(
      attachment.fileName,
      "attachment",
    );

    if (!confirmed) return;

    try {
      setAttachmentsLoading(true);
      const response = await apiRequest(`/Attachments/${attachment.id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete attachment");
      }

      showSuccessToast("Attachment deleted successfully");
      await fetchAttachments();
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleDeleteSignedCertificate = async () => {
    if (!signedCertificate) return;

    const confirmed = await showDeleteConfirm(
      "Signed Certificate",
      "signed certificate",
    );

    if (!confirmed) return;

    try {
      setSignedCertLoading(true);
      const response = await apiRequest(
        `/SignedCertificates/${signedCertificate.id}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete signed certificate");
      }

      showSuccessToast("Signed certificate deleted successfully");
      setSignedCertificate(null);
      await fetchData();
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setSignedCertLoading(false);
    }
  };

  // Set default nationality to Liberia when nationalities are loaded and no existing plaintiff
  useEffect(() => {
    if (
      nationalities.length > 0 &&
      !plaintiffApplicant &&
      plaintiffFormData.nationalityId === 0
    ) {
      const liberia = nationalities.find(
        (nat) => nat.name.toLowerCase() === DefaultNationality.toLowerCase(),
      );
      if (liberia) {
        setPlaintiffFormData((prev) => ({
          ...prev,
          nationalityId: liberia.id,
        }));
      }
    }
  }, [nationalities, plaintiffApplicant, plaintiffFormData.nationalityId]);

  // Set default nationality to Liberia for defendant as well
  useEffect(() => {
    if (
      nationalities.length > 0 &&
      !defendantApplicant &&
      defendantFormData.nationalityId === 0
    ) {
      const liberia = nationalities.find(
        (nat) => nat.name.toLowerCase() === DefaultNationality.toLowerCase(),
      );
      if (liberia) {
        setDefendantFormData((prev) => ({
          ...prev,
          nationalityId: liberia.id,
        }));
      }
    }
  }, [nationalities, defendantApplicant, defendantFormData.nationalityId]);

  const fetchLawyers = async () => {
    if (!id) return;
    try {
      const lawyers = await lawyerService.getLawyersByApplicationId(Number(id));
      const defendant =
        lawyers.find((l) => {
          const categoryValue = (l as any).category;
          if (categoryValue === LawyerCategory.Defendant) return true;
          if (typeof categoryValue === "string") {
            return categoryValue.toLowerCase() === "defendant";
          }
          return false;
        }) || null;
      const plaintiff =
        lawyers.find((l) => {
          const categoryValue = (l as any).category;
          if (categoryValue === LawyerCategory.Plaintiff) return true;
          if (typeof categoryValue === "string") {
            const normalized = categoryValue.toLowerCase();
            return normalized === "plaintiff" || normalized === "paintiff";
          }
          return false;
        }) || null;

      setDefendantLawyer(defendant);
      if (defendant) {
        setDefendantLawyerFormData({
          certificateApplicationId: defendant.certificateApplicationId,
          name: defendant.name || "",
          primaryPhone: defendant.primaryPhone || "",
          secondaryPhone: defendant.secondaryPhone || "",
          email: defendant.email || "",
          address: defendant.address || "",
          category: LawyerCategory.Defendant,
        });
        setDefendantLawyerAccordionOpen(true);
      }

      setPlaintiffLawyer(plaintiff);
      if (plaintiff) {
        setPlaintiffLawyerFormData({
          certificateApplicationId: plaintiff.certificateApplicationId,
          name: plaintiff.name || "",
          primaryPhone: plaintiff.primaryPhone || "",
          secondaryPhone: plaintiff.secondaryPhone || "",
          email: plaintiff.email || "",
          address: plaintiff.address || "",
          category: LawyerCategory.Plaintiff,
        });
        setPlaintiffLawyerAccordionOpen(true);
      }
    } catch (err: any) {
      console.error("Failed to load lawyers:", err);
    }
  };

  const fetchApplicants = async () => {
    if (!id) return;

    try {
      setPlaintiffLoading(true);
      const applicants =
        (await applicantService.getDivorceApplicantsByCertificateId(
          Number(id),
        )) as DivorceApplicantResponse[];

      const isPlaintiffApplicant = (app: DivorceApplicantResponse): boolean => {
        const typeDisplay = (app.applicantTypeDisplay || "")
          .toString()
          .toLowerCase();
        const typeValue = app.applicantType;

        if (typeDisplay.includes("plaintiff")) return true;
        if (typeDisplay.includes("plaintiff")) return true;

        if (typeof typeValue === "string") {
          const normalized = typeValue.toLowerCase();
          return normalized === "plaintiff" || normalized === "plaintiff";
        }

        if (typeof typeValue === "number") {
          return typeValue === 0;
        }

        return false;
      };

      const isDefendantApplicant = (app: DivorceApplicantResponse): boolean => {
        const typeDisplay = (app.applicantTypeDisplay || "")
          .toString()
          .toLowerCase();
        const typeValue = app.applicantType;

        if (typeDisplay.includes("defendant")) return true;
        if (typeDisplay.includes("defendant")) return true;

        if (typeof typeValue === "string") {
          const normalized = typeValue.toLowerCase();
          return normalized === "defendant" || normalized === "defendant";
        }

        if (typeof typeValue === "number") {
          return typeValue === 1;
        }

        return false;
      };

      // Find plaintiff record for Plaintiff's Detail accordion
      const plaintiff = applicants.find((app: DivorceApplicantResponse) =>
        isPlaintiffApplicant(app),
      );

      if (plaintiff) {
        const normalizedPlaintiff: Applicant = {
          id: plaintiff.id,
          userId: plaintiff.userId,
          firstName: plaintiff.firstName || "",
          middleName: plaintiff.middleName || "",
          lastName: plaintiff.lastName || "",
          previousName: plaintiff.previousName || "",
          genderId: plaintiff.genderId || 0,
          dateOfBirth: plaintiff.dateOfBirth || "",
          nationalIdNumber: plaintiff.nationalIdNumber || "",
          email: plaintiff.email || "",
          primaryPhone: plaintiff.primaryPhone || "",
          secondaryPhone: plaintiff.secondaryPhone || "",
          certificateApplicationId:
            plaintiff.certificateApplicationId || Number(id),
          applicantType:
            typeof plaintiff.applicantType === "number"
              ? plaintiff.applicantType
              : 2,
          countyOfOriginId: plaintiff.countyOfOriginId || 0,
          nationalityId: plaintiff.nationalityId || 0,
          birthCountyId: plaintiff.birthCountyId || 0,
          birthTown: plaintiff.birthTown || "",
          currentAddress: plaintiff.currentAddress || "",
          occupation: plaintiff.occupation || "",
          placeOfWork: plaintiff.placeOfWork || "",
          wasPreviouslyMarried: !!(
            plaintiff.wasPreviouslyMarried ||
            plaintiff.previousMarriageDate ||
            plaintiff.previousSpouseName
          ),
          previousMarriageDate: plaintiff.previousMarriageDate || "",
          previousSpouseName: plaintiff.previousSpouseName || "",
          fatherName: plaintiff.fatherName || "",
          motherName: plaintiff.motherName || "",
          profileImagePath: plaintiff.profileImagePath || "",
          photoUrl: plaintiff.photoUrl || "",
        };
        setPlaintiffApplicant(normalizedPlaintiff);

        // Automatically set wasPreviouslyMarried based on existence of previous marriage data
        const hasPreviousMarriageData = !!(
          plaintiff.previousMarriageDate || plaintiff.previousSpouseName
        );

        setPlaintiffFormData({
          userId: plaintiff.userId,
          firstName: plaintiff.firstName || "",
          middleName: plaintiff.middleName || "",
          lastName: plaintiff.lastName || "",
          previousName: plaintiff.previousName || "",
          genderId: plaintiff.genderId || 0,
          dateOfBirth: plaintiff.dateOfBirth
            ? plaintiff.dateOfBirth.split("T")[0]
            : "",
          nationalIdNumber: plaintiff.nationalIdNumber || "",
          email: plaintiff.email || "",
          primaryPhone: plaintiff.primaryPhone || "",
          secondaryPhone: plaintiff.secondaryPhone || "",
          certificateApplicationId:
            plaintiff.certificateApplicationId || Number(id),
          applicantType: "Plaintiff",
          countyOfOriginId: plaintiff.countyOfOriginId || 0,
          nationalityId: plaintiff.nationalityId || 0,
          birthCountyId: plaintiff.birthCountyId || 0,
          birthTown: plaintiff.birthTown || "",
          currentAddress: plaintiff.currentAddress || "",
          occupation: plaintiff.occupation || "",
          placeOfWork: plaintiff.placeOfWork || "",
          wasPreviouslyMarried: hasPreviousMarriageData,
          previousMarriageDate: plaintiff.previousMarriageDate
            ? plaintiff.previousMarriageDate.split("T")[0]
            : "",
          previousSpouseName: plaintiff.previousSpouseName || "",
          fatherName: plaintiff.fatherName || "",
          motherName: plaintiff.motherName || "",
          profileImagePath: plaintiff.profileImagePath || "",
        });
        // Set photo preview if exists
        const plaintiffPhotoPath =
          plaintiff.photoUrl || plaintiff.profileImagePath;
        const plaintiffPhotoUrl = getFullPath(plaintiffPhotoPath);
        if (plaintiffPhotoUrl) {
          setPlaintiffPhotoPreview(plaintiffPhotoUrl);
        }
        // Open accordion in edit mode when plaintiff record exists
        setPlaintiffAccordionOpen(true);
      }

      // Find defendant record for Defendant's Detail accordion
      const defendant = applicants.find((app: DivorceApplicantResponse) =>
        isDefendantApplicant(app),
      );

      if (defendant) {
        const normalizedDefendant: Applicant = {
          id: defendant.id,
          userId: defendant.userId,
          firstName: defendant.firstName || "",
          middleName: defendant.middleName || "",
          lastName: defendant.lastName || "",
          previousName: defendant.previousName || "",
          genderId: defendant.genderId || 0,
          dateOfBirth: defendant.dateOfBirth || "",
          nationalIdNumber: defendant.nationalIdNumber || "",
          email: defendant.email || "",
          primaryPhone: defendant.primaryPhone || "",
          secondaryPhone: defendant.secondaryPhone || "",
          certificateApplicationId:
            defendant.certificateApplicationId || Number(id),
          applicantType:
            typeof defendant.applicantType === "number"
              ? defendant.applicantType
              : 3,
          countyOfOriginId: defendant.countyOfOriginId || 0,
          nationalityId: defendant.nationalityId || 0,
          birthCountyId: defendant.birthCountyId || 0,
          birthTown: defendant.birthTown || "",
          currentAddress: defendant.currentAddress || "",
          occupation: defendant.occupation || "",
          placeOfWork: defendant.placeOfWork || "",
          wasPreviouslyMarried: !!(
            defendant.wasPreviouslyMarried ||
            defendant.previousMarriageDate ||
            defendant.previousSpouseName
          ),
          previousMarriageDate: defendant.previousMarriageDate || "",
          previousSpouseName: defendant.previousSpouseName || "",
          fatherName: defendant.fatherName || "",
          motherName: defendant.motherName || "",
          profileImagePath: defendant.profileImagePath || "",
          photoUrl: defendant.photoUrl || "",
        };
        setDefendantApplicant(normalizedDefendant);

        // Automatically set wasPreviouslyMarried based on existence of previous marriage data
        const hasPreviousMarriageData = !!(
          defendant.previousMarriageDate || defendant.previousSpouseName
        );

        setDefendantFormData({
          userId: defendant.userId,
          firstName: defendant.firstName || "",
          middleName: defendant.middleName || "",
          lastName: defendant.lastName || "",
          previousName: defendant.previousName || "",
          genderId: defendant.genderId || 0,
          dateOfBirth: defendant.dateOfBirth
            ? defendant.dateOfBirth.split("T")[0]
            : "",
          nationalIdNumber: defendant.nationalIdNumber || "",
          email: defendant.email || "",
          primaryPhone: defendant.primaryPhone || "",
          secondaryPhone: defendant.secondaryPhone || "",
          certificateApplicationId:
            defendant.certificateApplicationId || Number(id),
          applicantType: "Defendant",
          countyOfOriginId: defendant.countyOfOriginId || 0,
          nationalityId: defendant.nationalityId || 0,
          birthCountyId: defendant.birthCountyId || 0,
          birthTown: defendant.birthTown || "",
          currentAddress: defendant.currentAddress || "",
          occupation: defendant.occupation || "",
          placeOfWork: defendant.placeOfWork || "",
          wasPreviouslyMarried: hasPreviousMarriageData,
          previousMarriageDate: defendant.previousMarriageDate
            ? defendant.previousMarriageDate.split("T")[0]
            : "",
          previousSpouseName: defendant.previousSpouseName || "",
          fatherName: defendant.fatherName || "",
          motherName: defendant.motherName || "",
          profileImagePath: defendant.profileImagePath || "",
        });
        // Set photo preview if exists
        const defendantPhotoPath =
          defendant.photoUrl || defendant.profileImagePath;
        const defendantPhotoUrl = getFullPath(defendantPhotoPath);
        if (defendantPhotoUrl) {
          setDefendantPhotoPreview(defendantPhotoUrl);
        }
        // Open accordion in edit mode when defendant record exists
        setDefendantAccordionOpen(true);
      }
    } catch (err: any) {
      setPlaintiffError(err?.message || "Failed to load plaintiff details");
      setDefendantError(err?.message || "Failed to load defendant details");
    } finally {
      setPlaintiffLoading(false);
      setDefendantLoading(false);
    }
  };

  const handleDefendantFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const nextValue =
      name === "primaryPhone" || name === "secondaryPhone"
        ? sanitizePhoneNumberInput(value)
        : value;

    if (name === "genderId") {
      setDefendantFormData((prev) => ({
        ...prev,
        genderId: nextValue ? Number(nextValue) : 0,
      }));
    } else if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setDefendantFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "number") {
      setDefendantFormData((prev) => ({ ...prev, [name]: Number(nextValue) }));
    } else {
      setDefendantFormData((prev) => ({ ...prev, [name]: nextValue }));
    }

    // Clear validation error for this field when user starts typing
    if (defendantValidationErrors[name]) {
      setDefendantValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePlaintiffFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const nextValue =
      name === "primaryPhone" || name === "secondaryPhone"
        ? sanitizePhoneNumberInput(value)
        : value;

    if (name === "genderId") {
      setPlaintiffFormData((prev) => ({
        ...prev,
        genderId: nextValue ? Number(nextValue) : 0,
      }));
    } else if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setPlaintiffFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "number") {
      setPlaintiffFormData((prev) => ({ ...prev, [name]: Number(nextValue) }));
    } else {
      setPlaintiffFormData((prev) => ({ ...prev, [name]: nextValue }));
    }

    // Clear validation error for this field when user starts typing
    if (plaintiffValidationErrors[name]) {
      setPlaintiffValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePlaintiffPhotoChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setPlaintiffError("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setPlaintiffError("Image size must be less than 5MB");
        return;
      }
      try {
        setPhotoProcessing(true);
        const result = await generatePassportPhoto(file);
        setPlaintiffPhotoFile(result.file);
        setPlaintiffPhotoPreview(result.base64);
      } catch (err: any) {
        setPlaintiffError(err?.message || "Failed to process photo");
      } finally {
        setPhotoProcessing(false);
      }
    }
  };

  const removePlaintiffPhoto = () => {
    setPlaintiffPhotoFile(null);
    setPlaintiffPhotoPreview(null);
    setPlaintiffFormData({ ...plaintiffFormData, profileImagePath: "" });
  };

  const handleDefendantPhotoChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setDefendantError("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setDefendantError("Image size must be less than 5MB");
        return;
      }
      try {
        setPhotoProcessing(true);
        const result = await generatePassportPhoto(file);
        setDefendantPhotoFile(result.file);
        setDefendantPhotoPreview(result.base64);
      } catch (err: any) {
        setDefendantError(err?.message || "Failed to process photo");
      } finally {
        setPhotoProcessing(false);
      }
    }
  };

  const removeDefendantPhoto = () => {
    setDefendantPhotoFile(null);
    setDefendantPhotoPreview(null);
    setDefendantFormData({ ...defendantFormData, profileImagePath: "" });
  };

  const validateDefendantForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields validation
    if (!defendantFormData.firstName?.trim()) {
      errors.firstName = REQUIRED_FIELD_MESSAGE;
    }
    if (!defendantFormData.lastName?.trim()) {
      errors.lastName = REQUIRED_FIELD_MESSAGE;
    }
    if (!defendantFormData.dateOfBirth) {
      errors.dateOfBirth = REQUIRED_FIELD_MESSAGE;
    }
    const defendantPrimaryPhoneError = validatePhoneNumber(
      defendantFormData.primaryPhone,
      { required: true },
    );
    if (defendantPrimaryPhoneError) {
      errors.primaryPhone = defendantPrimaryPhoneError;
    }
    const defendantSecondaryPhoneError = validatePhoneNumber(
      defendantFormData.secondaryPhone,
    );
    if (defendantSecondaryPhoneError) {
      errors.secondaryPhone = defendantSecondaryPhoneError;
    }
    if (
      !defendantFormData.birthCountyId ||
      defendantFormData.birthCountyId === 0
    ) {
      errors.birthCountyId = REQUIRED_FIELD_MESSAGE;
    }
    if (!defendantFormData.birthTown?.trim()) {
      errors.birthTown = REQUIRED_FIELD_MESSAGE;
    }
    if (
      !defendantFormData.nationalityId ||
      defendantFormData.nationalityId === 0
    ) {
      errors.nationalityId = REQUIRED_FIELD_MESSAGE;
    }
    if (!defendantFormData.currentAddress?.trim()) {
      errors.currentAddress = REQUIRED_FIELD_MESSAGE;
    }
    // Photo is required for new applicants
    if (!defendantApplicant && !defendantPhotoFile) {
      errors.photo = REQUIRED_FIELD_MESSAGE;
    }

    setDefendantValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePlaintiffForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields validation
    if (!plaintiffFormData.firstName?.trim()) {
      errors.firstName = REQUIRED_FIELD_MESSAGE;
    }
    if (!plaintiffFormData.lastName?.trim()) {
      errors.lastName = REQUIRED_FIELD_MESSAGE;
    }
    if (!plaintiffFormData.dateOfBirth) {
      errors.dateOfBirth = REQUIRED_FIELD_MESSAGE;
    }
    const plaintiffPrimaryPhoneError = validatePhoneNumber(
      plaintiffFormData.primaryPhone,
      { required: true },
    );
    if (plaintiffPrimaryPhoneError) {
      errors.primaryPhone = plaintiffPrimaryPhoneError;
    }
    const plaintiffSecondaryPhoneError = validatePhoneNumber(
      plaintiffFormData.secondaryPhone,
    );
    if (plaintiffSecondaryPhoneError) {
      errors.secondaryPhone = plaintiffSecondaryPhoneError;
    }
    if (
      !plaintiffFormData.birthCountyId ||
      plaintiffFormData.birthCountyId === 0
    ) {
      errors.birthCountyId = REQUIRED_FIELD_MESSAGE;
    }
    if (!plaintiffFormData.birthTown?.trim()) {
      errors.birthTown = REQUIRED_FIELD_MESSAGE;
    }
    if (
      !plaintiffFormData.nationalityId ||
      plaintiffFormData.nationalityId === 0
    ) {
      errors.nationalityId = REQUIRED_FIELD_MESSAGE;
    }
    if (!plaintiffFormData.currentAddress?.trim()) {
      errors.currentAddress = REQUIRED_FIELD_MESSAGE;
    }
    // Photo is required for new applicants
    if (!plaintiffApplicant && !plaintiffPhotoFile) {
      errors.photo = REQUIRED_FIELD_MESSAGE;
    }

    setPlaintiffValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDefendantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDefendantError("");

    if (!application) return;

    // Validate form
    if (!validateDefendantForm()) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Defendant Information",
      "Are you sure you want to submit the defendant's information?",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setDefendantLoading(true);

      const {
        countyOfOriginId,
        wasPreviouslyMarried,
        previousMarriageDate,
        previousSpouseName,
        fatherName,
        motherName,
        profileImagePath,
        ...cleanedData
      } = defendantFormData;

      if (defendantApplicant) {
        // UPDATE existing defendant
        const payload = {
          ...cleanedData,
          id: defendantApplicant.id,
          userId: defendantApplicant.userId,
          certificateApplicationId: application.id,
          applicantType: "Defendant",
          roleId: BasicRole,
        };
        await applicantService.updateDivorceApplicant(
          defendantApplicant.id!,
          payload as UpdateApplicantDto,
          defendantPhotoFile || undefined,
        );
        showSuccessToast("Defendant details updated successfully");
      } else {
        // CREATE new defendant
        const payload = {
          ...cleanedData,
          certificateApplicationId: application.id,
          applicantType: "Defendant",
          roleId: BasicRole,
        };
        await applicantService.createDivorceApplicant(
          payload as CreateApplicantDto,
          defendantPhotoFile || undefined,
        );
        showSuccessToast("Defendant details saved successfully");
      }

      // Refresh applicant data
      await fetchApplicants();
      // Refresh submission requirements
      await checkReadyForSubmission(application.id!);
    } catch (err: any) {
      setDefendantError(err?.message || "Failed to save defendant details");
    } finally {
      setDefendantLoading(false);
    }
  };

  const handlePlaintiffSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPlaintiffError("");

    if (!application) return;

    // Validate form
    if (!validatePlaintiffForm()) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Plaintiff Information",
      "Are you sure you want to submit the plaintiff's information?",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setPlaintiffLoading(true);

      const {
        countyOfOriginId,
        wasPreviouslyMarried,
        previousMarriageDate,
        previousSpouseName,
        fatherName,
        motherName,
        profileImagePath,
        ...cleanedData
      } = plaintiffFormData;

      if (plaintiffApplicant) {
        // UPDATE existing plaintiff
        const payload = {
          ...cleanedData,
          id: plaintiffApplicant.id,
          userId: plaintiffApplicant.userId,
          certificateApplicationId: application.id,
          applicantType: "Plaintiff",
          roleId: BasicRole,
        };
        await applicantService.updateDivorceApplicant(
          plaintiffApplicant.id!,
          payload as UpdateApplicantDto,
          plaintiffPhotoFile || undefined,
        );
        showSuccessToast("Plaintiff details updated successfully");
      } else {
        // CREATE new plaintiff
        const payload = {
          ...cleanedData,
          certificateApplicationId: application.id,
          applicantType: "Plaintiff",
          roleId: BasicRole,
        };
        await applicantService.createDivorceApplicant(
          payload as CreateApplicantDto,
          plaintiffPhotoFile || undefined,
        );
        showSuccessToast("Plaintiff details saved successfully");
      }

      // Refresh applicant data
      await fetchApplicants();
      // Refresh submission requirements
      await checkReadyForSubmission(application.id!);
    } catch (err: any) {
      setPlaintiffError(err?.message || "Failed to save plaintiff details");
    } finally {
      setPlaintiffLoading(false);
    }
  };

  const handleDeletePlaintiff = async () => {
    if (!plaintiffApplicant) return;

    const confirmed = await showDeleteConfirm(
      plaintiffApplicant.firstName + " " + plaintiffApplicant.lastName,
      "plaintiff applicant",
    );

    if (!confirmed) return;

    try {
      setPlaintiffLoading(true);
      await applicantService.deleteApplicant(plaintiffApplicant.id!);
      showSuccessToast("Plaintiff details deleted successfully");
      // Refresh submission requirements
      await checkReadyForSubmission(application?.id!);
      setPlaintiffApplicant(null);
      // Reset photo state
      setPlaintiffPhotoFile(null);
      setPlaintiffPhotoPreview(null);
      // Reset form to initial state
      setPlaintiffFormData({
        userId: undefined,
        firstName: "",
        middleName: "",
        lastName: "",
        genderId: 0,
        dateOfBirth: "",
        nationalIdNumber: "",
        email: "",
        primaryPhone: "",
        secondaryPhone: "",
        certificateApplicationId: application?.id || 0,
        applicantType: "Plaintiff",
        countyOfOriginId: 0,
        nationalityId: 0,
        birthCountyId: 0,
        birthTown: "",
        currentAddress: "",
        occupation: "",
        placeOfWork: "",
        wasPreviouslyMarried: false,
        previousMarriageDate: "",
        previousSpouseName: "",
        fatherName: "",
        motherName: "",
      });
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setPlaintiffLoading(false);
    }
  };

  const handleDeleteDefendant = async () => {
    if (!defendantApplicant) return;

    const confirmed = await showDeleteConfirm(
      defendantApplicant.firstName + " " + defendantApplicant.lastName,
      "defendant applicant",
    );

    if (!confirmed) return;

    try {
      setDefendantLoading(true);
      await applicantService.deleteApplicant(defendantApplicant.id!);
      showSuccessToast("Defendant details deleted successfully");
      // Refresh submission requirements
      await checkReadyForSubmission(application?.id!);
      setDefendantApplicant(null);
      // Reset photo state
      setDefendantPhotoFile(null);
      setDefendantPhotoPreview(null);
      // Reset form to initial state
      setDefendantFormData({
        userId: undefined,
        firstName: "",
        middleName: "",
        lastName: "",
        genderId: 0,
        dateOfBirth: "",
        nationalIdNumber: "",
        email: "",
        primaryPhone: "",
        secondaryPhone: "",
        certificateApplicationId: application?.id || 0,
        applicantType: "Defendant",
        countyOfOriginId: 0,
        nationalityId: 0,
        birthCountyId: 0,
        birthTown: "",
        currentAddress: "",
        occupation: "",
        placeOfWork: "",
        wasPreviouslyMarried: false,
        previousMarriageDate: "",
        previousSpouseName: "",
        fatherName: "",
        motherName: "",
      });
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setDefendantLoading(false);
    }
  };

  const handleDefendantLawyerFormChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    const nextValue =
      name === "primaryPhone" || name === "secondaryPhone"
        ? sanitizePhoneNumberInput(value)
        : value;
    setDefendantLawyerFormData((prev) => ({ ...prev, [name]: nextValue }));
    if (defendantLawyerValidationErrors[name]) {
      setDefendantLawyerValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateDefendantLawyerForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!defendantLawyerFormData.name?.trim()) {
      errors.name = REQUIRED_FIELD_MESSAGE;
    }
    const defendantLawyerPrimaryPhoneError = validatePhoneNumber(
      defendantLawyerFormData.primaryPhone,
      { required: true },
    );
    if (defendantLawyerPrimaryPhoneError) {
      errors.primaryPhone = defendantLawyerPrimaryPhoneError;
    }
    const defendantLawyerSecondaryPhoneError = validatePhoneNumber(
      defendantLawyerFormData.secondaryPhone,
    );
    if (defendantLawyerSecondaryPhoneError) {
      errors.secondaryPhone = defendantLawyerSecondaryPhoneError;
    }
    if (!defendantLawyerFormData.address?.trim()) {
      errors.address = REQUIRED_FIELD_MESSAGE;
    }
    setDefendantLawyerValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDefendantLawyerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDefendantLawyerError("");
    if (!application) return;
    if (!validateDefendantLawyerForm()) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Defendant Lawyer Information",
      "Are you sure you want to submit the defendant lawyer's information?",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setDefendantLawyerLoading(true);
      if (defendantLawyer) {
        await lawyerService.updateLawyer(defendantLawyer.id!, {
          id: defendantLawyer.id!,
          certificateApplicationId: application.id!,
          name: defendantLawyerFormData.name,
          primaryPhone: defendantLawyerFormData.primaryPhone,
          secondaryPhone: defendantLawyerFormData.secondaryPhone,
          email: defendantLawyerFormData.email,
          address: defendantLawyerFormData.address,
          category: "Defendant" as any,
        });
        showSuccessToast("Defendant Lawyer Details updated successfully");
      } else {
        await lawyerService.createLawyer({
          certificateApplicationId: application.id!,
          name: defendantLawyerFormData.name,
          primaryPhone: defendantLawyerFormData.primaryPhone,
          secondaryPhone: defendantLawyerFormData.secondaryPhone,
          email: defendantLawyerFormData.email,
          address: defendantLawyerFormData.address,
          category: "Defendant" as any,
        });
        showSuccessToast("Defendant Lawyer Details saved successfully");
      }
      await fetchLawyers();
      await checkReadyForSubmission(application.id!);
    } catch (err: any) {
      setDefendantLawyerError(
        err?.message || "Failed to save Defendant Lawyer Details",
      );
    } finally {
      setDefendantLawyerLoading(false);
    }
  };

  const handleDeleteDefendantLawyer = async () => {
    if (!defendantLawyer) return;
    const confirmed = await showDeleteConfirm(
      defendantLawyer.name,
      "Defendant Lawyer Details",
    );
    if (!confirmed) return;
    try {
      setDefendantLawyerLoading(true);
      await lawyerService.deleteLawyer(defendantLawyer.id!);
      showSuccessToast("Defendant Lawyer Details deleted successfully");
      await checkReadyForSubmission(application?.id!);
      setDefendantLawyer(null);
      setDefendantLawyerFormData({
        certificateApplicationId: application?.id || 0,
        name: "",
        primaryPhone: "",
        secondaryPhone: "",
        email: "",
        address: "",
        category: LawyerCategory.Defendant,
      });
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setDefendantLawyerLoading(false);
    }
  };

  const handlePlaintiffLawyerFormChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    const nextValue =
      name === "primaryPhone" || name === "secondaryPhone"
        ? sanitizePhoneNumberInput(value)
        : value;
    setPlaintiffLawyerFormData((prev) => ({ ...prev, [name]: nextValue }));
    if (plaintiffLawyerValidationErrors[name]) {
      setPlaintiffLawyerValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validatePlaintiffLawyerForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!plaintiffLawyerFormData.name?.trim()) {
      errors.name = REQUIRED_FIELD_MESSAGE;
    }
    const plaintiffLawyerPrimaryPhoneError = validatePhoneNumber(
      plaintiffLawyerFormData.primaryPhone,
      { required: true },
    );
    if (plaintiffLawyerPrimaryPhoneError) {
      errors.primaryPhone = plaintiffLawyerPrimaryPhoneError;
    }
    const plaintiffLawyerSecondaryPhoneError = validatePhoneNumber(
      plaintiffLawyerFormData.secondaryPhone,
    );
    if (plaintiffLawyerSecondaryPhoneError) {
      errors.secondaryPhone = plaintiffLawyerSecondaryPhoneError;
    }
    if (!plaintiffLawyerFormData.address?.trim()) {
      errors.address = REQUIRED_FIELD_MESSAGE;
    }
    setPlaintiffLawyerValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePlaintiffLawyerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPlaintiffLawyerError("");
    if (!application) return;
    if (!validatePlaintiffLawyerForm()) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Plaintiff Lawyer Information",
      "Are you sure you want to submit the plaintiff lawyer's information?",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setPlaintiffLawyerLoading(true);
      if (plaintiffLawyer) {
        await lawyerService.updateLawyer(plaintiffLawyer.id!, {
          id: plaintiffLawyer.id!,
          certificateApplicationId: application.id!,
          name: plaintiffLawyerFormData.name,
          primaryPhone: plaintiffLawyerFormData.primaryPhone,
          secondaryPhone: plaintiffLawyerFormData.secondaryPhone,
          email: plaintiffLawyerFormData.email,
          address: plaintiffLawyerFormData.address,
          category: "Plaintiff" as any,
        });
        showSuccessToast("Plaintiff Lawyer Details updated successfully");
      } else {
        await lawyerService.createLawyer({
          certificateApplicationId: application.id!,
          name: plaintiffLawyerFormData.name,
          primaryPhone: plaintiffLawyerFormData.primaryPhone,
          secondaryPhone: plaintiffLawyerFormData.secondaryPhone,
          email: plaintiffLawyerFormData.email,
          address: plaintiffLawyerFormData.address,
          category: "Plaintiff" as any,
        });
        showSuccessToast("Plaintiff Lawyer Details saved successfully");
      }
      await fetchLawyers();
      await checkReadyForSubmission(application.id!);
    } catch (err: any) {
      setPlaintiffLawyerError(
        err?.message || "Failed to save Plaintiff Lawyer Details",
      );
    } finally {
      setPlaintiffLawyerLoading(false);
    }
  };

  const handleDeletePlaintiffLawyer = async () => {
    if (!plaintiffLawyer) return;
    const confirmed = await showDeleteConfirm(
      plaintiffLawyer.name,
      "Plaintiff Lawyer Details",
    );
    if (!confirmed) return;
    try {
      setPlaintiffLawyerLoading(true);
      await lawyerService.deleteLawyer(plaintiffLawyer.id!);
      showSuccessToast("Plaintiff Lawyer Details deleted successfully");
      await checkReadyForSubmission(application?.id!);
      setPlaintiffLawyer(null);
      setPlaintiffLawyerFormData({
        certificateApplicationId: application?.id || 0,
        name: "",
        primaryPhone: "",
        secondaryPhone: "",
        email: "",
        address: "",
        category: LawyerCategory.Plaintiff,
      });
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setPlaintiffLawyerLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!application) return;

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Application",
      "Are you sure you want to submit this application? This action cannot be undone.",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      await certificateService.changeStatus(
        application.id!,
        UserStatusId.Submitted,
      );
      showSuccessToast("Application submitted successfully");
      // Send email notification if settings allow
      if (application.workflowId) {
        notificationService
          .sendEmailNotification(application.workflowId, application.id!)
          .catch(() => {});
      }
      // Refresh the application data to show updated status
      await fetchData();
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!application || !application.workflowId) return;
    const confirmed = await showConfirm(
      "Approve Application",
      `Are you sure you want to approve this divorce certificate application?`,
      "Yes, approve",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);

      // Get workflow details to check if this is the final stage
      const workflow = await workflowService.getWorkflowById(
        application.workflowId,
      );
      const totalStages = workflow.stages?.length || 0;
      const isFinalStage = currentStageOrder >= totalStages;

      // Approve the workflow action
      await workflowService.approve({
        workflowId: application.workflowId,
        recordId: application.id!,
        entityName: "CertificateApplications",
        comments: "Application approved",
      });

      // If this is the final approval stage, update status to Approved
      if (isFinalStage) {
        await certificateService.changeStatus(
          application.id!,
          UserStatusId.Approved,
        );
      }

      await fetchData();
      showSuccessToast("Application has been approved successfully.");
      // Send email notification if settings allow
      notificationService
        .sendEmailNotification(application.workflowId, application.id!)
        .catch(() => {});
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleQuery = () => {
    if (!application || !application.workflowId) return;
    setShowQueryForm(true);
  };

  const handleSubmitQuery = async (payload: QueryWorkflowActionDto) => {
    if (!application || !application.workflowId) return;
    try {
      setActionLoading(true);

      await workflowService.query(payload);

      // Change status to Queried to move the workflow back one step
      await certificateService.changeStatus(
        application.id!,
        UserStatusId.Queried,
      );

      await fetchData();
      showSuccessToast("Application has been queried successfully.");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!application || !application.workflowId) return;

    // Import Swal for custom input dialog
    const Swal = (await import("sweetalert2")).default;

    const result = await Swal.fire({
      title: "Reject Application",
      html: `
        <p>Please provide a reason for rejecting this application:</p>
      `,
      input: "textarea",
      inputPlaceholder: "Enter your rejection reason here...",
      inputAttributes: {
        "aria-label": "Rejection reason",
        style: "min-height: 100px;",
      },
      showCancelButton: true,
      confirmButtonText: "Reject Application",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      inputValidator: (value) => {
        if (!value || value.trim() === "") {
          return "You must provide a reason for rejection!";
        }
      },
    });

    if (!result.isConfirmed || !result.value || !result.value.trim()) return;

    const rejectComment = result.value.trim();

    try {
      setActionLoading(true);

      // Reject the workflow action with the provided comment
      await workflowService.reject({
        workflowId: application.workflowId,
        recordId: application.id!,
        entityName: "CertificateApplications",
        comments: rejectComment,
      });

      // Change status to Rejected
      await certificateService.changeStatus(
        application.id!,
        UserStatusId.Rejected,
      );

      await fetchData();
      showSuccessToast("Application has been rejected successfully.");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const openEditModal = () => {
    if (!application) return;

    const enforcedCountyId =
      isRoleCountyLinked && linkedCountyId
        ? linkedCountyId
        : application.countyId;

    setEditFormData({
      category: application.category,
      email: application.email || "",
      placeOfWedding: application.placeOfWedding || "",
      dateOfWedding: application.dateOfWedding?.split("T")[0] || "",
      countyId: enforcedCountyId,
      workflowId: application.workflowId,
      dateOfCertification: application.dateOfCertification?.split("T")[0],
      processAtHeadquarters: isRoleCountyLinked
        ? false
        : application.processAtHeadquarters || false,
    });
    setShowEditModal(true);
  };

  const resetEditForm = () => {
    setEditFormData({});
    setEditError("");
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError("");
    if (!application || !application.id) {
      setEditError("Application ID is missing");
      return;
    }

    try {
      setEditLoading(true);

      // Include the id and formNo in the payload as required by the API
      const updatePayload: UpdateCertificateApplicationDto = {
        id: application.id,
        category: editFormData.category,
        email: editFormData.email,
        placeOfWedding: editFormData.placeOfWedding,
        countyId: editFormData.countyId,
        workflowId: editFormData.workflowId,
        processAtHeadquarters: editFormData.processAtHeadquarters,
        dateOfWedding: editFormData.dateOfWedding,
        dateOfCertification: editFormData.dateOfCertification,
        lraPayment: editServiceCharge?.lraPayment ?? application.lraPayment,
        operationalCost:
          editServiceCharge?.operationalCost ?? application.operationalCost,
        currencyId: editServiceCharge?.currencyId ?? application.currencyId,
        statusId: application.statusId,
      };

      // Convert date strings to ISO format if they exist
      if (updatePayload.dateOfWedding) {
        // If it's just a date (YYYY-MM-DD), convert to ISO datetime
        if (updatePayload.dateOfWedding.length === 10) {
          updatePayload.dateOfWedding = new Date(
            updatePayload.dateOfWedding,
          ).toISOString();
        }
      }

      if (updatePayload.dateOfCertification) {
        // If it's just a date (YYYY-MM-DD), convert to ISO datetime
        if (updatePayload.dateOfCertification.length === 10) {
          updatePayload.dateOfCertification = new Date(
            updatePayload.dateOfCertification,
          ).toISOString();
        }
      }

      console.log("Update Payload:", updatePayload);
      console.log("Application ID in URL:", application.id);

      await certificateService.updateCertificateApplication(
        application.id,
        updatePayload,
      );
      setShowEditModal(false);
      resetEditForm();
      await fetchData();
      showSuccessToast("Application updated successfully.");
    } catch (err: any) {
      console.error("Update error:", err);
      setEditError(err?.message || "Failed to update application");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!application) return;
    const confirmed = await showDeleteConfirm(
      application.formNo,
      "divorce certificate application",
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      await certificateService.deleteCertificateApplication(application.id!);
      showSuccessToast("Application has been deleted successfully.");
      navigate(getBackPath());
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
      setActionLoading(false);
    }
  };

  const handlePayment = (method: string) => {
    setPaymentDropdownOpen(false);

    // Show payment modal or navigate
    switch (method) {
      case "Manual":
        openManualPaymentModal();
        break;
      case "Card":
        openCardPaymentModal();
        break;
      case "Mobile Money":
        window.open("https://paymentcollectclient.triaxo.com/", "_blank");
        break;
      default:
        showSuccessToast(`Redirecting to ${method} payment...`);
    }
  };

  const openCardPaymentModal = () => {
    if (!application?.id) return;
    // CardPayment modal uses Stripe and doesn't need lookup data
    setShowCardPaymentModal(true);
  };

  const openManualPaymentModal = () => {
    if (!application?.id) return;
    setShowManualPaymentModal(true);
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  if (!HasPermission(ModuleId.Divorces, ApplicationPermissionId.View)) {
    return (
      <div
        className={`dashboard-body ${
          sidebarOpen ? "sidebar-open" : "sidebar-closed"
        }`}
      >
        <Topbar
          onMenuToggle={() => setSidebarOpen((prev) => !prev)}
          sidebarOpen={sidebarOpen}
        />
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content">
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>
              You do not have permission to view divorce certificate
              applications
            </span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      {!isPrintPage && (
        <>
          <Topbar
            onMenuToggle={() => setSidebarOpen((prev) => !prev)}
            sidebarOpen={sidebarOpen}
          />
          <Sidebar isOpen={sidebarOpen} />
        </>
      )}
      <main className="main-content">
        <div className="user-detail-page">
          <BusyOverlay
            isVisible={loading || actionLoading || photoProcessing}
            message={
              photoProcessing
                ? "Processing photo — removing background & resizing..."
                : actionLoading
                  ? "Processing..."
                  : "Loading application details..."
            }
          />

          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span>{error}</span>
              <button onClick={() => setError("")} className="alert-close">
                ✕
              </button>
            </div>
          )}

          {/* Print View - Marriage Certificate */}
          {isPrintPage &&
            application &&
            plaintiffApplicant &&
            defendantApplicant && (
              <>
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    background: "#fff",
                  }}
                  className="no-print"
                >
                  <button
                    onClick={() => window.print()}
                    style={{
                      padding: "12px 32px",
                      fontSize: "16px",
                      background: "#3b82f6",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: "600",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    🖨️ Print Certificate
                  </button>
                  <button
                    onClick={() => navigate(getBackPath())}
                    style={{
                      padding: "12px 32px",
                      fontSize: "16px",
                      background: "#6b7280",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: "600",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      marginLeft: "10px",
                    }}
                  >
                    ← Back
                  </button>
                </div>
                <DivorceCert
                  plaintiffName={`${plaintiffApplicant.firstName} ${plaintiffApplicant.middleName || ""} ${plaintiffApplicant.lastName}`.trim()}
                  plaintiffDateOfBirth={plaintiffApplicant.dateOfBirth || ""}
                  plaintiffPlaceOfBirth={plaintiffApplicant.birthTown || ""}
                  plaintiffCountyOfBirth={
                    counties.find(
                      (c) => c.id === plaintiffApplicant.birthCountyId,
                    )?.name || ""
                  }
                  plaintiffFatherName={plaintiffApplicant.fatherName || ""}
                  plaintiffMotherName={plaintiffApplicant.motherName || ""}
                  defendantName={`${defendantApplicant.firstName} ${defendantApplicant.middleName || ""} ${defendantApplicant.lastName}`.trim()}
                  defendantDateOfBirth={defendantApplicant.dateOfBirth || ""}
                  defendantPlaceOfBirth={defendantApplicant.birthTown || ""}
                  defendantCountyOfBirth={
                    counties.find(
                      (c) => c.id === defendantApplicant.birthCountyId,
                    )?.name || ""
                  }
                  defendantFatherName={defendantApplicant.fatherName || ""}
                  defendantMotherName={defendantApplicant.motherName || ""}
                  placeOfWedding={application.placeOfWedding || ""}
                  dateOfWedding={application.dateOfWedding || ""}
                  countyName={application.countyName || ""}
                  certificateCode={application.formNo || ""}
                  revenueNumber=""
                  ministryName={ministryName}
                  plaintiffPhotoUrl={
                    plaintiffApplicant.photoUrl ||
                    plaintiffApplicant.profileImagePath
                  }
                  defendantPhotoUrl={
                    defendantApplicant.photoUrl ||
                    defendantApplicant.profileImagePath
                  }
                  certificateApplicationId={application.id}
                />
              </>
            )}

          {application && !isPrintPage && (
            <div className="user-detail-layout">
              <button
                className="user-detail-back user-detail-back--mobile"
                onClick={() => navigate(getBackPath())}
              >
                ← Back to Applications
              </button>

              {/* Column 1: Application Details */}
              <div className="user-detail-info">
                {/* Profile header */}
                <div className="user-detail-profile-header">
                  <div className="user-detail-avatar">📄</div>
                  <div className="user-detail-profile-info">
                    <h2>{application.formNo}</h2>
                    <div className="user-detail-profile-meta">
                      <span>{application.category}</span>
                      <span className="divider">?</span>
                      <span className={`status-badge status-badge--active`}>
                        {application.status?.name ||
                          application.statusName ||
                          "Pending"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Divorce Application Information */}
                <h3 className="user-detail-section-title">
                  Divorce Application Information
                </h3>
                <div
                  className="user-detail-grid"
                  style={{
                    gridTemplateColumns: "repeat(3, 1fr)",
                  }}
                >
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Category</div>
                    <div className="user-detail-field-value">
                      {application.category}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">County</div>
                    <div
                      className={`user-detail-field-value ${!application.countyName ? "empty" : ""}`}
                    >
                      {application.countyName || "?"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">
                      Place of Divorce
                    </div>
                    <div
                      className={`user-detail-field-value ${!application.placeOfWedding ? "empty" : ""}`}
                    >
                      {application.placeOfWedding || "?"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">
                      Date of Marriage
                    </div>
                    <div
                      className={`user-detail-field-value ${!application.dateOfWedding ? "empty" : ""}`}
                    >
                      {formatDate(application.dateOfWedding) || "?"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">
                      Date of Divorce
                    </div>
                    <div
                      className={`user-detail-field-value ${!application.dateOfCertification ? "empty" : ""}`}
                    >
                      {formatDate(application.dateOfCertification) || "_"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Email</div>
                    <div
                      className={`user-detail-field-value ${!application.email ? "empty" : ""}`}
                    >
                      {application.email || "_"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Created Date</div>
                    <div
                      className={`user-detail-field-value ${!application.createdAt ? "empty" : ""}`}
                    >
                      {formatDate(application.createdAt) || "_"}
                    </div>
                  </div>
                </div>

                {/* Signed Certificate Display (Finalize Page Only) */}
                {isFinalizePage && signedCertificate && (
                  <div style={{ marginTop: "2rem" }}>
                    <div
                      style={{
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        marginBottom: "1rem",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "1rem",
                          marginBottom: "0.5rem",
                          color: "#28a745",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>📄 Signed Certificate</span>
                        <button
                          onClick={handleDeleteSignedCertificate}
                          disabled={signedCertLoading}
                          style={{
                            background: "#dc3545",
                            color: "#fff",
                            border: "none",
                            padding: "0.5rem 1rem",
                            borderRadius: "4px",
                            cursor: signedCertLoading
                              ? "not-allowed"
                              : "pointer",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            opacity: signedCertLoading ? 0.6 : 1,
                          }}
                          title="Delete signed certificate"
                        >
                          {signedCertLoading ? (
                            <>
                              <span className="spinner spinner--sm"></span>
                              Deleting...
                            </>
                          ) : (
                            <>
                              <span>🗑️</span> Delete
                            </>
                          )}
                        </button>
                      </div>
                      <div
                        style={{
                          fontSize: "0.875rem",
                          color: "#6b7280",
                          marginBottom: "1rem",
                        }}
                      >
                        Uploaded on:{" "}
                        {new Date(signedCertificate.createdAt).toLocaleString()}
                      </div>
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                          overflow: "hidden",
                          background: "#fff",
                        }}
                      >
                        {signedCertificate.documentPath ? (
                          (() => {
                            const fullUrl = getFullPath(
                              signedCertificate.documentPath,
                            );
                            if (!fullUrl) {
                              return (
                                <div
                                  style={{
                                    padding: "2rem",
                                    textAlign: "center",
                                    color: "#6b7280",
                                  }}
                                >
                                  Unable to display certificate
                                </div>
                              );
                            }
                            return signedCertificate.documentPath
                              .toLowerCase()
                              .endsWith(".pdf") ? (
                              <iframe
                                src={fullUrl || undefined}
                                title="Signed Certificate"
                                style={{
                                  width: "100%",
                                  height: "600px",
                                  border: "none",
                                }}
                              />
                            ) : (
                              <img
                                src={fullUrl || undefined}
                                alt="Signed Certificate"
                                style={{
                                  width: "100%",
                                  maxHeight: "800px",
                                  objectFit: "contain",
                                }}
                              />
                            );
                          })()
                        ) : (
                          <div
                            style={{
                              padding: "2rem",
                              textAlign: "center",
                              color: "#6b7280",
                            }}
                          >
                            Unable to display certificate
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Plaintiff's Detail Accordion */}
                {!isPrintPage && (
                  <div style={{ marginTop: "2rem" }}>
                    <div
                      onClick={() =>
                        setPlaintiffAccordionOpen(!plaintiffAccordionOpen)
                      }
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "600",
                        fontSize: "1rem",
                      }}
                    >
                      <span>Plaintiff's Detail</span>
                      <span>{plaintiffAccordionOpen ? "▼" : "▶"}</span>
                    </div>
                    {plaintiffAccordionOpen && (
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          padding: "1.5rem",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        <form onSubmit={handlePlaintiffSubmit}>
                          {plaintiffError && (
                            <div
                              style={{
                                padding: "0.75rem",
                                marginBottom: "1rem",
                                background: "#fee",
                                color: "#c00",
                                borderRadius: "4px",
                                border: "1px solid #fcc",
                              }}
                            >
                              {plaintiffError}
                            </div>
                          )}

                          {/* Photo Upload */}
                          <div
                            style={{
                              marginBottom: "1.5rem",
                            }}
                          >
                            <label
                              style={{
                                display: "block",
                                marginBottom: "0.5rem",
                                fontWeight: "500",
                                fontSize: "0.875rem",
                                color: "#374151",
                              }}
                            >
                              Passport Size Photo{" "}
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <div className="photo-upload-container">
                              {plaintiffPhotoPreview ? (
                                <div className="photo-preview">
                                  <img
                                    src={plaintiffPhotoPreview || undefined}
                                    alt="Plaintiff Preview"
                                    className="preview-image"
                                    onError={(e) => {
                                      const img = e.target as HTMLImageElement;
                                      img.style.border = "2px solid red";
                                      img.alt = "Failed to load image";
                                    }}
                                  />
                                  {!isFinalizePage && (
                                    <button
                                      type="button"
                                      className="btn btn--sm btn--danger"
                                      onClick={removePlaintiffPhoto}
                                    >
                                      Remove Photo
                                    </button>
                                  )}
                                </div>
                              ) : (
                                !isFinalizePage && (
                                  <div className="photo-upload-area">
                                    <input
                                      type="file"
                                      id="plaintiffPhotoUpload"
                                      accept="image/*"
                                      onChange={handlePlaintiffPhotoChange}
                                      style={{ display: "none" }}
                                    />
                                    <label
                                      htmlFor="plaintiffPhotoUpload"
                                      className="photo-upload-label"
                                    >
                                      <div className="upload-icon">📷</div>
                                      <div className="upload-text">
                                        <span className="upload-title">
                                          Click to upload photo
                                        </span>
                                        <span className="upload-subtitle">
                                          PNG, JPG up to 5MB
                                        </span>
                                      </div>
                                    </label>
                                  </div>
                                )
                              )}
                            </div>
                            {plaintiffValidationErrors.photo && (
                              <div className="form-error">
                                {plaintiffValidationErrors.photo}
                              </div>
                            )}
                          </div>

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${plaintiffFormData.firstName ? "has-value" : ""}`}
                            >
                              <label className="form-label">First Name *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="firstName"
                                value={plaintiffFormData.firstName}
                                onChange={handlePlaintiffFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {plaintiffValidationErrors.firstName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {plaintiffValidationErrors.firstName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${plaintiffFormData.middleName ? "has-value" : ""}`}
                            >
                              <label className="form-label">Middle Name</label>
                              <input
                                type="text"
                                className="form-input"
                                name="middleName"
                                value={plaintiffFormData.middleName}
                                onChange={handlePlaintiffFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${plaintiffFormData.lastName ? "has-value" : ""}`}
                            >
                              <label className="form-label">Last Name *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="lastName"
                                value={plaintiffFormData.lastName}
                                onChange={handlePlaintiffFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {plaintiffValidationErrors.lastName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {plaintiffValidationErrors.lastName}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">Gender *</label>
                              <select
                                className="form-input"
                                name="genderId"
                                value={plaintiffFormData.genderId || ""}
                                onChange={handlePlaintiffFormChange}
                                required
                              >
                                <option value="">Select Gender</option>
                                <option value={1}>Male</option>
                                <option value={2}>Female</option>
                              </select>
                            </div>
                            <div
                              className={`form-group ${plaintiffFormData.previousName ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Previous Name (If Any)
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="previousName"
                                value={plaintiffFormData.previousName || ""}
                                onChange={handlePlaintiffFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">
                                Date of Birth *
                              </label>
                              <input
                                type="date"
                                className="form-input"
                                name="dateOfBirth"
                                value={plaintiffFormData.dateOfBirth}
                                onChange={handlePlaintiffFormChange}
                                max={getMaxDateFor18Plus()}
                                required
                                readOnly={isFinalizePage}
                              />
                              {plaintiffValidationErrors.dateOfBirth && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {plaintiffValidationErrors.dateOfBirth}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${plaintiffFormData.nationalIdNumber ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                National ID Number
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="nationalIdNumber"
                                value={plaintiffFormData.nationalIdNumber}
                                onChange={handlePlaintiffFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${plaintiffFormData.primaryPhone ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Primary Phone *
                              </label>
                              <input
                                type="tel"
                                className="form-input"
                                name="primaryPhone"
                                value={plaintiffFormData.primaryPhone}
                                onChange={handlePlaintiffFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {plaintiffValidationErrors.primaryPhone && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {plaintiffValidationErrors.primaryPhone}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${plaintiffFormData.secondaryPhone ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Secondary Phone
                              </label>
                              <input
                                type="tel"
                                className="form-input"
                                name="secondaryPhone"
                                value={plaintiffFormData.secondaryPhone}
                                onChange={handlePlaintiffFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {plaintiffValidationErrors.secondaryPhone && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {plaintiffValidationErrors.secondaryPhone}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">
                                Birth County *
                              </label>
                              <select
                                className="form-input"
                                name="birthCountyId"
                                value={plaintiffFormData.birthCountyId}
                                onChange={handlePlaintiffFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select County</option>
                                {counties.map((county) => (
                                  <option key={county.id} value={county.id}>
                                    {county.name}
                                  </option>
                                ))}
                              </select>
                              {plaintiffValidationErrors.birthCountyId && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {plaintiffValidationErrors.birthCountyId}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${plaintiffFormData.birthTown ? "has-value" : ""}`}
                            >
                              <label className="form-label">Birth Town *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="birthTown"
                                value={plaintiffFormData.birthTown}
                                onChange={handlePlaintiffFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {plaintiffValidationErrors.birthTown && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {plaintiffValidationErrors.birthTown}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">
                                Nationality *
                              </label>
                              <select
                                className="form-input"
                                name="nationalityId"
                                value={plaintiffFormData.nationalityId}
                                onChange={handlePlaintiffFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select Nationality</option>
                                {nationalities.map((nationality) => (
                                  <option
                                    key={nationality.id}
                                    value={nationality.id}
                                  >
                                    {nationality.name}
                                  </option>
                                ))}
                              </select>
                              {plaintiffValidationErrors.nationalityId && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {plaintiffValidationErrors.nationalityId}
                                </div>
                              )}
                            </div>
                          </div>

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${plaintiffFormData.email ? "has-value" : ""}`}
                            >
                              <label className="form-label">Email</label>
                              <input
                                type="email"
                                className="form-input"
                                name="email"
                                value={plaintiffFormData.email}
                                onChange={handlePlaintiffFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>

                            <div
                              className={`form-group ${plaintiffFormData.currentAddress ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Current Address *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="currentAddress"
                                value={plaintiffFormData.currentAddress}
                                onChange={handlePlaintiffFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {plaintiffValidationErrors.currentAddress && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {plaintiffValidationErrors.currentAddress}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${plaintiffFormData.occupation ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Occupation/Profession
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="occupation"
                                value={plaintiffFormData.occupation || ""}
                                onChange={handlePlaintiffFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${plaintiffFormData.placeOfWork ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Place of Work
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="placeOfWork"
                                value={plaintiffFormData.placeOfWork || ""}
                                onChange={handlePlaintiffFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                          </div>

                          {!isFinalizePage && (
                            <div
                              style={{
                                display: "flex",
                                gap: "0.75rem",
                                justifyContent: "flex-end",
                              }}
                            >
                              {plaintiffApplicant && (
                                <button
                                  type="button"
                                  onClick={handleDeletePlaintiff}
                                  disabled={plaintiffLoading}
                                  style={{
                                    padding: "0.65rem 1.5rem",
                                    background: plaintiffLoading
                                      ? "#ccc"
                                      : "#dc3545",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: plaintiffLoading
                                      ? "not-allowed"
                                      : "pointer",
                                    fontWeight: 500,
                                  }}
                                >
                                  Delete Plaintiff
                                </button>
                              )}
                              <button
                                type="submit"
                                disabled={plaintiffLoading}
                                style={{
                                  padding: "0.65rem 1.5rem",
                                  background: plaintiffLoading
                                    ? "#ccc"
                                    : "#007bff",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: plaintiffLoading
                                    ? "not-allowed"
                                    : "pointer",
                                  fontWeight: 500,
                                }}
                              >
                                {plaintiffLoading
                                  ? "Saving..."
                                  : plaintiffApplicant
                                    ? "Update Plaintiff"
                                    : "Save Plaintiff"}
                              </button>
                            </div>
                          )}
                        </form>
                      </div>
                    )}
                  </div>
                )}

                {/* Plaintiff Lawyer's Detail Accordion */}
                {!isPrintPage && (
                  <div style={{ marginTop: "1rem" }}>
                    <div
                      onClick={() =>
                        setPlaintiffLawyerAccordionOpen(
                          !plaintiffLawyerAccordionOpen,
                        )
                      }
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "600",
                        fontSize: "1rem",
                      }}
                    >
                      <span>Plaintiff Lawyer's Detail</span>
                      <span>{plaintiffLawyerAccordionOpen ? "▼" : "▶"}</span>
                    </div>
                    {plaintiffLawyerAccordionOpen && (
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          padding: "1.5rem",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        <form onSubmit={handlePlaintiffLawyerSubmit}>
                          {plaintiffLawyerError && (
                            <div
                              style={{
                                padding: "0.75rem",
                                marginBottom: "1rem",
                                background: "#fee",
                                color: "#c00",
                                borderRadius: "4px",
                                border: "1px solid #fcc",
                              }}
                            >
                              {plaintiffLawyerError}
                            </div>
                          )}

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${plaintiffLawyerFormData.name ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Lawyer/Firm Name *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="name"
                                value={plaintiffLawyerFormData.name}
                                onChange={handlePlaintiffLawyerFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {plaintiffLawyerValidationErrors.name && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {plaintiffLawyerValidationErrors.name}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${plaintiffLawyerFormData.primaryPhone ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Primary Phone *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="primaryPhone"
                                value={plaintiffLawyerFormData.primaryPhone}
                                onChange={handlePlaintiffLawyerFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {plaintiffLawyerValidationErrors.primaryPhone && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {plaintiffLawyerValidationErrors.primaryPhone}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${plaintiffLawyerFormData.secondaryPhone ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Secondary Phone
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="secondaryPhone"
                                value={
                                  plaintiffLawyerFormData.secondaryPhone || ""
                                }
                                onChange={handlePlaintiffLawyerFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {plaintiffLawyerValidationErrors.secondaryPhone && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {
                                    plaintiffLawyerValidationErrors.secondaryPhone
                                  }
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${plaintiffLawyerFormData.email ? "has-value" : ""}`}
                            >
                              <label className="form-label">Email</label>
                              <input
                                type="email"
                                className="form-input"
                                name="email"
                                value={plaintiffLawyerFormData.email || ""}
                                onChange={handlePlaintiffLawyerFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group lawyer-address-field ${plaintiffLawyerFormData.address ? "has-value" : ""}`}
                            >
                              <label className="form-label">Address *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="address"
                                value={plaintiffLawyerFormData.address}
                                onChange={handlePlaintiffLawyerFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {plaintiffLawyerValidationErrors.address && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {plaintiffLawyerValidationErrors.address}
                                </div>
                              )}
                            </div>
                          </div>

                          {!isFinalizePage && (
                            <div
                              style={{
                                display: "flex",
                                gap: "0.75rem",
                                justifyContent: "flex-end",
                              }}
                            >
                              {plaintiffLawyer && (
                                <button
                                  type="button"
                                  onClick={handleDeletePlaintiffLawyer}
                                  disabled={plaintiffLawyerLoading}
                                  style={{
                                    padding: "0.65rem 1.5rem",
                                    background: plaintiffLawyerLoading
                                      ? "#ccc"
                                      : "#dc3545",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: plaintiffLawyerLoading
                                      ? "not-allowed"
                                      : "pointer",
                                    fontWeight: 500,
                                  }}
                                >
                                  Delete Lawyer
                                </button>
                              )}
                              <button
                                type="submit"
                                disabled={plaintiffLawyerLoading}
                                style={{
                                  padding: "0.65rem 1.5rem",
                                  background: plaintiffLawyerLoading
                                    ? "#ccc"
                                    : "#007bff",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: plaintiffLawyerLoading
                                    ? "not-allowed"
                                    : "pointer",
                                  fontWeight: 500,
                                }}
                              >
                                {plaintiffLawyerLoading
                                  ? "Saving..."
                                  : plaintiffLawyer
                                    ? "Update Lawyer"
                                    : "Save Lawyer"}
                              </button>
                            </div>
                          )}
                        </form>
                      </div>
                    )}
                  </div>
                )}

                {/* Defendant's Detail Accordion */}
                {!isPrintPage && (
                  <div style={{ marginTop: "1rem" }}>
                    <div
                      onClick={() =>
                        setDefendantAccordionOpen(!defendantAccordionOpen)
                      }
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "600",
                        fontSize: "1rem",
                      }}
                    >
                      <span>Defendant's Detail</span>
                      <span>{defendantAccordionOpen ? "▼" : "▶"}</span>
                    </div>
                    {defendantAccordionOpen && (
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          padding: "1.5rem",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        <form onSubmit={handleDefendantSubmit}>
                          {defendantError && (
                            <div
                              style={{
                                padding: "0.75rem",
                                marginBottom: "1rem",
                                background: "#fee",
                                color: "#c00",
                                borderRadius: "4px",
                                border: "1px solid #fcc",
                              }}
                            >
                              {defendantError}
                            </div>
                          )}

                          {/* Photo Upload */}
                          <div
                            style={{
                              marginBottom: "1.5rem",
                            }}
                          >
                            <label
                              style={{
                                display: "block",
                                marginBottom: "0.5rem",
                                fontWeight: "500",
                                fontSize: "0.875rem",
                                color: "#374151",
                              }}
                            >
                              Passport Size Photo{" "}
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <div className="photo-upload-container">
                              {defendantPhotoPreview ? (
                                <div className="photo-preview">
                                  <img
                                    src={defendantPhotoPreview || undefined}
                                    alt="Defendant Preview"
                                    className="preview-image"
                                    onError={(e) => {
                                      const img = e.target as HTMLImageElement;
                                      img.style.border = "2px solid red";
                                      img.alt = "Failed to load image";
                                    }}
                                  />
                                  {!isFinalizePage && (
                                    <button
                                      type="button"
                                      className="btn btn--sm btn--danger"
                                      onClick={removeDefendantPhoto}
                                    >
                                      Remove Photo
                                    </button>
                                  )}
                                </div>
                              ) : (
                                !isFinalizePage && (
                                  <div className="photo-upload-area">
                                    <input
                                      type="file"
                                      id="defendantPhotoUpload"
                                      accept="image/*"
                                      onChange={handleDefendantPhotoChange}
                                      style={{ display: "none" }}
                                    />
                                    <label
                                      htmlFor="defendantPhotoUpload"
                                      className="photo-upload-label"
                                    >
                                      <div className="upload-icon">📷</div>
                                      <div className="upload-text">
                                        <span className="upload-title">
                                          Click to upload photo
                                        </span>
                                        <span className="upload-subtitle">
                                          PNG, JPG up to 5MB
                                        </span>
                                      </div>
                                    </label>
                                  </div>
                                )
                              )}
                            </div>
                            {defendantValidationErrors.photo && (
                              <div className="form-error">
                                {defendantValidationErrors.photo}
                              </div>
                            )}
                          </div>

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${defendantFormData.firstName ? "has-value" : ""}`}
                            >
                              <label className="form-label">First Name *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="firstName"
                                value={defendantFormData.firstName}
                                onChange={handleDefendantFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {defendantValidationErrors.firstName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {defendantValidationErrors.firstName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${defendantFormData.middleName ? "has-value" : ""}`}
                            >
                              <label className="form-label">Middle Name</label>
                              <input
                                type="text"
                                className="form-input"
                                name="middleName"
                                value={defendantFormData.middleName}
                                onChange={handleDefendantFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${defendantFormData.lastName ? "has-value" : ""}`}
                            >
                              <label className="form-label">Last Name *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="lastName"
                                value={defendantFormData.lastName}
                                onChange={handleDefendantFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {defendantValidationErrors.lastName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {defendantValidationErrors.lastName}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">Gender *</label>
                              <select
                                className="form-input"
                                name="genderId"
                                value={defendantFormData.genderId || ""}
                                onChange={handleDefendantFormChange}
                                required
                              >
                                <option value="">Select Gender</option>
                                <option value={1}>Male</option>
                                <option value={2}>Female</option>
                              </select>
                            </div>
                            <div
                              className={`form-group ${defendantFormData.previousName ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Previous Name (If Any)
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="previousName"
                                value={defendantFormData.previousName || ""}
                                onChange={handleDefendantFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">
                                Date of Birth *
                              </label>
                              <input
                                type="date"
                                className="form-input"
                                name="dateOfBirth"
                                value={defendantFormData.dateOfBirth}
                                onChange={handleDefendantFormChange}
                                max={getMaxDateFor18Plus()}
                                required
                                readOnly={isFinalizePage}
                              />
                              {defendantValidationErrors.dateOfBirth && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {defendantValidationErrors.dateOfBirth}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${defendantFormData.nationalIdNumber ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                National ID Number
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="nationalIdNumber"
                                value={defendantFormData.nationalIdNumber}
                                onChange={handleDefendantFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${defendantFormData.primaryPhone ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Primary Phone *
                              </label>
                              <input
                                type="tel"
                                className="form-input"
                                name="primaryPhone"
                                value={defendantFormData.primaryPhone}
                                onChange={handleDefendantFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {defendantValidationErrors.primaryPhone && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {defendantValidationErrors.primaryPhone}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${defendantFormData.secondaryPhone ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Secondary Phone
                              </label>
                              <input
                                type="tel"
                                className="form-input"
                                name="secondaryPhone"
                                value={defendantFormData.secondaryPhone}
                                onChange={handleDefendantFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {defendantValidationErrors.secondaryPhone && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {defendantValidationErrors.secondaryPhone}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">
                                Birth County *
                              </label>
                              <select
                                className="form-input"
                                name="birthCountyId"
                                value={defendantFormData.birthCountyId}
                                onChange={handleDefendantFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select County</option>
                                {counties.map((county) => (
                                  <option key={county.id} value={county.id}>
                                    {county.name}
                                  </option>
                                ))}
                              </select>
                              {defendantValidationErrors.birthCountyId && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {defendantValidationErrors.birthCountyId}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${defendantFormData.birthTown ? "has-value" : ""}`}
                            >
                              <label className="form-label">Birth Town *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="birthTown"
                                value={defendantFormData.birthTown}
                                onChange={handleDefendantFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {defendantValidationErrors.birthTown && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {defendantValidationErrors.birthTown}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">
                                Nationality *
                              </label>
                              <select
                                className="form-input"
                                name="nationalityId"
                                value={defendantFormData.nationalityId}
                                onChange={handleDefendantFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select Nationality</option>
                                {nationalities.map((nationality) => (
                                  <option
                                    key={nationality.id}
                                    value={nationality.id}
                                  >
                                    {nationality.name}
                                  </option>
                                ))}
                              </select>
                              {defendantValidationErrors.nationalityId && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {defendantValidationErrors.nationalityId}
                                </div>
                              )}
                            </div>
                          </div>

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${defendantFormData.email ? "has-value" : ""}`}
                            >
                              <label className="form-label">Email</label>
                              <input
                                type="email"
                                className="form-input"
                                name="email"
                                value={defendantFormData.email}
                                onChange={handleDefendantFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>

                            <div
                              className={`form-group ${defendantFormData.currentAddress ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Current Address *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="currentAddress"
                                value={defendantFormData.currentAddress}
                                onChange={handleDefendantFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {defendantValidationErrors.currentAddress && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {defendantValidationErrors.currentAddress}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${defendantFormData.occupation ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Occupation/Profession
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="occupation"
                                value={defendantFormData.occupation || ""}
                                onChange={handleDefendantFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${defendantFormData.placeOfWork ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Place of Work
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="placeOfWork"
                                value={defendantFormData.placeOfWork || ""}
                                onChange={handleDefendantFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                          </div>

                          {!isFinalizePage && (
                            <div
                              style={{
                                display: "flex",
                                gap: "0.75rem",
                                justifyContent: "flex-end",
                              }}
                            >
                              {defendantApplicant && (
                                <button
                                  type="button"
                                  onClick={handleDeleteDefendant}
                                  disabled={defendantLoading}
                                  style={{
                                    padding: "0.65rem 1.5rem",
                                    background: defendantLoading
                                      ? "#ccc"
                                      : "#dc3545",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: defendantLoading
                                      ? "not-allowed"
                                      : "pointer",
                                    fontWeight: 500,
                                  }}
                                >
                                  Delete Defendant
                                </button>
                              )}
                              <button
                                type="submit"
                                disabled={defendantLoading}
                                style={{
                                  padding: "0.65rem 1.5rem",
                                  background: defendantLoading
                                    ? "#ccc"
                                    : "#007bff",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: defendantLoading
                                    ? "not-allowed"
                                    : "pointer",
                                  fontWeight: 500,
                                }}
                              >
                                {defendantLoading
                                  ? "Saving..."
                                  : defendantApplicant
                                    ? "Update Defendant"
                                    : "Save Defendant"}
                              </button>
                            </div>
                          )}
                        </form>
                      </div>
                    )}
                  </div>
                )}

                {/* Defendant Lawyer's Detail Accordion */}
                {!isPrintPage && (
                  <div style={{ marginTop: "1rem" }}>
                    <div
                      onClick={() =>
                        setDefendantLawyerAccordionOpen(
                          !defendantLawyerAccordionOpen,
                        )
                      }
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "600",
                        fontSize: "1rem",
                      }}
                    >
                      <span>Defendant Lawyer's Detail</span>
                      <span>{defendantLawyerAccordionOpen ? "▼" : "▶"}</span>
                    </div>
                    {defendantLawyerAccordionOpen && (
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          padding: "1.5rem",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        <form onSubmit={handleDefendantLawyerSubmit}>
                          {defendantLawyerError && (
                            <div
                              style={{
                                padding: "0.75rem",
                                marginBottom: "1rem",
                                background: "#fee",
                                color: "#c00",
                                borderRadius: "4px",
                                border: "1px solid #fcc",
                              }}
                            >
                              {defendantLawyerError}
                            </div>
                          )}

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${defendantLawyerFormData.name ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Lawyer/Firm Name *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="name"
                                value={defendantLawyerFormData.name}
                                onChange={handleDefendantLawyerFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {defendantLawyerValidationErrors.name && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {defendantLawyerValidationErrors.name}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${defendantLawyerFormData.primaryPhone ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Primary Phone *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="primaryPhone"
                                value={defendantLawyerFormData.primaryPhone}
                                onChange={handleDefendantLawyerFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {defendantLawyerValidationErrors.primaryPhone && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {defendantLawyerValidationErrors.primaryPhone}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${defendantLawyerFormData.secondaryPhone ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Secondary Phone
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="secondaryPhone"
                                value={
                                  defendantLawyerFormData.secondaryPhone || ""
                                }
                                onChange={handleDefendantLawyerFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {defendantLawyerValidationErrors.secondaryPhone && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {
                                    defendantLawyerValidationErrors.secondaryPhone
                                  }
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${defendantLawyerFormData.email ? "has-value" : ""}`}
                            >
                              <label className="form-label">Email</label>
                              <input
                                type="email"
                                className="form-input"
                                name="email"
                                value={defendantLawyerFormData.email || ""}
                                onChange={handleDefendantLawyerFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group lawyer-address-field ${defendantLawyerFormData.address ? "has-value" : ""}`}
                            >
                              <label className="form-label">Address *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="address"
                                value={defendantLawyerFormData.address}
                                onChange={handleDefendantLawyerFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {defendantLawyerValidationErrors.address && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {defendantLawyerValidationErrors.address}
                                </div>
                              )}
                            </div>
                          </div>

                          {!isFinalizePage && (
                            <div
                              style={{
                                display: "flex",
                                gap: "0.75rem",
                                justifyContent: "flex-end",
                              }}
                            >
                              {defendantLawyer && (
                                <button
                                  type="button"
                                  onClick={handleDeleteDefendantLawyer}
                                  disabled={defendantLawyerLoading}
                                  style={{
                                    padding: "0.65rem 1.5rem",
                                    background: defendantLawyerLoading
                                      ? "#ccc"
                                      : "#dc3545",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: defendantLawyerLoading
                                      ? "not-allowed"
                                      : "pointer",
                                    fontWeight: 500,
                                  }}
                                >
                                  Delete Lawyer
                                </button>
                              )}
                              <button
                                type="submit"
                                disabled={defendantLawyerLoading}
                                style={{
                                  padding: "0.65rem 1.5rem",
                                  background: defendantLawyerLoading
                                    ? "#ccc"
                                    : "#007bff",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: defendantLawyerLoading
                                    ? "not-allowed"
                                    : "pointer",
                                  fontWeight: 500,
                                }}
                              >
                                {defendantLawyerLoading
                                  ? "Saving..."
                                  : defendantLawyer
                                    ? "Update Lawyer"
                                    : "Save Lawyer"}
                              </button>
                            </div>
                          )}
                        </form>
                      </div>
                    )}
                  </div>
                )}

                {/* Attachments Accordion */}
                {!isPrintPage && (
                  <div style={{ marginTop: "1rem" }}>
                    <div
                      onClick={() =>
                        setAttachmentsAccordionOpen(!attachmentsAccordionOpen)
                      }
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "600",
                        fontSize: "1rem",
                      }}
                    >
                      <span>Attachments</span>
                      <span>{attachmentsAccordionOpen ? "▼" : "▶"}</span>
                    </div>
                    {attachmentsAccordionOpen && (
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          padding: "1.5rem",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        {showDocumentScanModal && application && (
                          <DocumentScan
                            application={application}
                            onClose={() => setShowDocumentScanModal(false)}
                            onScanSuccess={() => {
                              fetchData();
                              fetchAttachments();
                            }}
                          />
                        )}
                        {!showDocumentScanModal && (
                          <>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "1rem",
                              }}
                            >
                              <h4
                                style={{
                                  margin: 0,
                                  fontSize: "0.95rem",
                                  fontWeight: "600",
                                  color: "#374151",
                                }}
                              >
                                Uploaded Documents ({attachments.length})
                              </h4>
                              {!isFinalizePage && (
                                <button
                                  className="btn btn--primary"
                                  onClick={() => setShowDocumentScanModal(true)}
                                  style={{
                                    background: "#28a745",
                                    color: "#fff",
                                    border: "none",
                                    padding: "0.5rem 1rem",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "0.875rem",
                                    fontWeight: "500",
                                  }}
                                >
                                  <span style={{ marginRight: "0.5rem" }}>
                                    ➕
                                  </span>
                                  Add Document
                                </button>
                              )}
                            </div>

                            {attachmentsLoading ? (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "2rem",
                                  color: "#6b7280",
                                }}
                              >
                                Loading attachments...
                              </div>
                            ) : attachments.length > 0 ? (
                              <div style={{ overflowX: "auto" }}>
                                <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  <thead>
                                    <tr
                                      style={{
                                        background: "#f8f9fa",
                                        borderBottom: "2px solid #dee2e6",
                                      }}
                                    >
                                      <th
                                        style={{
                                          padding: "0.75rem",
                                          textAlign: "left",
                                          fontWeight: "600",
                                          color: "#374151",
                                        }}
                                      >
                                        Document Type
                                      </th>
                                      <th
                                        style={{
                                          padding: "0.75rem",
                                          textAlign: "left",
                                          fontWeight: "600",
                                          color: "#374151",
                                        }}
                                      >
                                        Description
                                      </th>
                                      <th
                                        style={{
                                          padding: "0.75rem",
                                          textAlign: "center",
                                          fontWeight: "600",
                                          color: "#374151",
                                          width: "100px",
                                        }}
                                      >
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {attachments.map((attachment) => (
                                      <React.Fragment key={attachment.id}>
                                        <tr
                                          style={{
                                            borderBottom:
                                              viewingAttachmentId ===
                                              attachment.id
                                                ? "none"
                                                : "1px solid #dee2e6",
                                          }}
                                        >
                                          <td style={{ padding: "0.75rem" }}>
                                            {attachment.documentTypeName || "?"}
                                          </td>
                                          <td style={{ padding: "0.75rem" }}>
                                            {attachment.description || "?"}
                                          </td>
                                          <td
                                            style={{
                                              padding: "0.75rem",
                                              textAlign: "center",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                gap: "0.5rem",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <button
                                                onClick={() => {
                                                  const newId =
                                                    viewingAttachmentId ===
                                                    attachment.id
                                                      ? null
                                                      : attachment.id;
                                                  setViewingAttachmentId(newId);
                                                }}
                                                title={
                                                  viewingAttachmentId ===
                                                  attachment.id
                                                    ? "Hide document"
                                                    : "View document"
                                                }
                                                style={{
                                                  background: "transparent",
                                                  border: "none",
                                                  cursor: "pointer",
                                                  fontSize: "1.25rem",
                                                  padding: "0.25rem",
                                                  color:
                                                    viewingAttachmentId ===
                                                    attachment.id
                                                      ? "#10b981"
                                                      : "#3b82f6",
                                                }}
                                              >
                                                {viewingAttachmentId ===
                                                attachment.id
                                                  ? "👁️‍🗨️"
                                                  : "👁️"}
                                              </button>
                                              {!isFinalizePage && (
                                                <button
                                                  onClick={() =>
                                                    handleDeleteAttachment(
                                                      attachment,
                                                    )
                                                  }
                                                  title="Delete attachment"
                                                  style={{
                                                    background: "transparent",
                                                    border: "none",
                                                    cursor: "pointer",
                                                    fontSize: "1.25rem",
                                                    padding: "0.25rem",
                                                    color: "#ef4444",
                                                  }}
                                                >
                                                  🗑️
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                        {viewingAttachmentId ===
                                          attachment.id && (
                                          <tr>
                                            <td
                                              colSpan={3}
                                              style={{
                                                padding: 0,
                                                borderBottom:
                                                  "1px solid #dee2e6",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  background: "#f8f9fa",
                                                  padding: "1rem",
                                                  borderTop:
                                                    "2px solid #3b82f6",
                                                }}
                                              >
                                                {attachment.description && (
                                                  <div
                                                    style={{
                                                      marginBottom: "0.75rem",
                                                      padding: "0.5rem",
                                                      background: "#fff",
                                                      borderRadius: "4px",
                                                      fontSize: "0.875rem",
                                                      color: "#6b7280",
                                                    }}
                                                  >
                                                    <strong>
                                                      Description:
                                                    </strong>{" "}
                                                    {attachment.description}
                                                  </div>
                                                )}
                                                {(() => {
                                                  // Validate attachment has documentPath
                                                  if (
                                                    !attachment.documentPath
                                                  ) {
                                                    return (
                                                      <div
                                                        style={{
                                                          padding: "1rem",
                                                          background: "#fee",
                                                          color: "#c00",
                                                          borderRadius: "4px",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        <strong>Error:</strong>{" "}
                                                        Document file path is
                                                        missing
                                                      </div>
                                                    );
                                                  }

                                                  const baseUrl =
                                                    BASE_URL.replace(
                                                      "/api",
                                                      "",
                                                    );
                                                  const documentPath =
                                                    attachment.documentPath.startsWith(
                                                      "/",
                                                    )
                                                      ? attachment.documentPath.substring(
                                                          1,
                                                        )
                                                      : attachment.documentPath;
                                                  const fullUrl = `${baseUrl}/${documentPath}`;

                                                  return (
                                                    <div>
                                                      <div
                                                        style={{
                                                          marginBottom:
                                                            "0.5rem",
                                                          padding: "0.5rem",
                                                          background: "#fff",
                                                          borderRadius: "4px",
                                                          fontSize: "0.75rem",
                                                          color: "#6b7280",
                                                          wordBreak:
                                                            "break-all",
                                                        }}
                                                      >
                                                        <strong>
                                                          Document URL:
                                                        </strong>{" "}
                                                        {fullUrl}
                                                      </div>
                                                      <iframe
                                                        key={attachment.id}
                                                        src={
                                                          getFullPath(
                                                            fullUrl,
                                                          ) || undefined
                                                        }
                                                        title={
                                                          attachment.documentTypeName ||
                                                          "Document"
                                                        }
                                                        style={{
                                                          width: "100%",
                                                          height: "600px",
                                                          border:
                                                            "1px solid #dee2e6",
                                                          borderRadius: "4px",
                                                          background: "#fff",
                                                        }}
                                                      />
                                                    </div>
                                                  );
                                                })()}
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "2rem",
                                  background: "#f8f9fa",
                                  borderRadius: "4px",
                                  color: "#6b7280",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "2rem",
                                    marginBottom: "0.5rem",
                                  }}
                                >
                                  📁
                                </div>
                                <div
                                  style={{
                                    fontWeight: "500",
                                    marginBottom: "0.25rem",
                                  }}
                                >
                                  No documents uploaded yet
                                </div>
                                <div style={{ fontSize: "0.875rem" }}>
                                  Click "Add Document" to upload your first
                                  document
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Column 2: Action Buttons */}
              <div className="user-detail-actions">
                <button
                  className="user-detail-back user-detail-back--desktop"
                  onClick={() => navigate(getBackPath())}
                >
                  ← Back to Applications
                </button>

                {!isPrintPage &&
                  signedCertificate &&
                  HasPermission(
                    ModuleId.Divorces,
                    ApplicationPermissionId.View,
                  ) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--primary"
                      onClick={() => setShowSignatureExtractionModal(true)}
                      title="Extract signatures from certificate"
                      style={{
                        background: "#007bff",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">✍️</span> Extract Signatures
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Divorces,
                    ApplicationPermissionId.Apply,
                  ) &&
                  canShowMakePayment &&
                  !hasExistingPayment && (
                    <div style={{ position: "relative" }}>
                      <button
                        className="user-detail-action-btn user-detail-action-btn--warning"
                        onClick={() =>
                          setPaymentDropdownOpen(!paymentDropdownOpen)
                        }
                        title="Make payment"
                        style={{
                          background: "#ffc107",
                          color: "#000",
                        }}
                      >
                        <span className="action-icon">💳</span> Make Payment
                        <span style={{ marginLeft: "0.5rem" }}>▼</span>
                      </button>
                      {paymentDropdownOpen && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            marginTop: "0.25rem",
                            background: "#fff",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            zIndex: 1000,
                            overflow: "hidden",
                          }}
                        >
                          <button
                            onClick={() => handlePayment("Manual")}
                            style={{
                              width: "100%",
                              padding: "0.75rem 1rem",
                              background: "transparent",
                              border: "none",
                              borderBottom: "1px solid #dee2e6",
                              textAlign: "left",
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#f8f9fa")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            <span style={{ marginRight: "0.5rem" }}>📝</span>{" "}
                            Manual Payment
                          </button>
                          <button
                            onClick={() => handlePayment("Card")}
                            style={{
                              width: "100%",
                              padding: "0.75rem 1rem",
                              background: "transparent",
                              border: "none",
                              borderBottom: "1px solid #dee2e6",
                              textAlign: "left",
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#f8f9fa")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            <span style={{ marginRight: "0.5rem" }}>💳</span>{" "}
                            Card Payment
                          </button>
                          <button
                            onClick={() => handlePayment("Mobile Money")}
                            style={{
                              width: "100%",
                              padding: "0.75rem 1rem",
                              background: "transparent",
                              border: "none",
                              textAlign: "left",
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#f8f9fa")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            <span style={{ marginRight: "0.5rem" }}>📱</span>{" "}
                            Mobile Money
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Divorces,
                    ApplicationPermissionId.Edit,
                  ) &&
                  canShowSubmitAction &&
                  (application?.statusId === UserStatusId.Created ||
                    application?.statusId === UserStatusId.Queried) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--info"
                      onClick={handleSubmit}
                      title="Submit application"
                      style={{
                        background: "#17a2b8",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">📋</span> Submit
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Divorces,
                    ApplicationPermissionId.Approve,
                  ) &&
                  application?.workflowId &&
                  isAtMyApprovalLevel &&
                  (application?.statusId === UserStatusId.Submitted ||
                    application?.statusId === UserStatusId.Queried ||
                    application?.statusId === UserStatusId.InProgress) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--success"
                      onClick={handleApprove}
                      title="Approve application"
                      style={{
                        background: "#28a745",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">✅</span> Approve
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Divorces,
                    ApplicationPermissionId.Approve,
                  ) &&
                  application?.workflowId &&
                  isAtMyApprovalLevel &&
                  (application?.statusId === UserStatusId.Submitted ||
                    application?.statusId === UserStatusId.Queried ||
                    application?.statusId === UserStatusId.InProgress) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--warning"
                      onClick={handleQuery}
                      title="Query application"
                      style={{
                        background: "#ffc107",
                        color: "#000",
                      }}
                    >
                      <span className="action-icon">❓</span> Query
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Divorces,
                    ApplicationPermissionId.Approve,
                  ) &&
                  application?.workflowId &&
                  isAtMyApprovalLevel &&
                  (application?.statusId === UserStatusId.Submitted ||
                    application?.statusId === UserStatusId.Queried ||
                    application?.statusId === UserStatusId.InProgress) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--danger"
                      onClick={handleReject}
                      title="Reject application"
                      style={{
                        background: "#dc3545",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">❌</span> Reject
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Divorces,
                    ApplicationPermissionId.Edit,
                  ) &&
                  (application?.statusId === UserStatusId.Created ||
                    application?.statusId === UserStatusId.Queried) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--primary"
                      onClick={openEditModal}
                      title="Edit this application"
                    >
                      <span className="action-icon">✏️</span> Edit
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Divorces,
                    ApplicationPermissionId.Delete,
                  ) &&
                  (application?.statusId === UserStatusId.Created ||
                    application?.statusId === UserStatusId.Queried) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--danger"
                      onClick={handleDelete}
                    >
                      <span className="action-icon">🗑️</span> Delete
                    </button>
                  )}

                <button
                  className="user-detail-action-btn"
                  onClick={() => setShowHistory(true)}
                >
                  <span className="action-icon">🕘</span> History
                </button>
                {hasQueryRemarks && (
                  <button
                    className="user-detail-action-btn user-detail-action-btn--warning"
                    onClick={() => setShowQueryRemarks(true)}
                  >
                    <span className="action-icon">💬</span> Query Remarks
                  </button>
                )}

                {/* Workflow Progress */}
                {!isPrintPage &&
                  application?.workflowId &&
                  (application?.statusId === UserStatusId.Submitted ||
                    application?.statusId === UserStatusId.Queried ||
                    application?.statusId === UserStatusId.InProgress) && (
                    <div style={{ marginTop: "2rem" }}>
                      <WorkflowStages
                        workflowId={application.workflowId}
                        currentStageOrder={currentStageOrder}
                      />
                    </div>
                  )}

                {/* Required to Submit Section */}
                {!isPrintPage &&
                  (application?.statusId === UserStatusId.Created ||
                    application?.statusId === UserStatusId.Queried) &&
                  Object.keys(submissionRequirements).length > 0 && (
                    <div
                      style={{
                        marginTop: "1.5rem",
                        padding: "1rem",
                        background: isReadyForSubmission
                          ? "#d4edda"
                          : "#f8f9fa",
                        border: `1px solid ${
                          isReadyForSubmission ? "#c3e6cb" : "#dee2e6"
                        }`,
                        borderRadius: "4px",
                      }}
                    >
                      <h4
                        style={{
                          margin: "0 0 0.75rem 0",
                          fontSize: "0.875rem",
                          fontWeight: "600",
                          color: isReadyForSubmission ? "#155724" : "#495057",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {isReadyForSubmission
                          ? "✅ Ready to Submit"
                          : "Required to Submit"}
                      </h4>
                      <div style={{ fontSize: "0.875rem" }}>
                        {Object.entries(submissionRequirements).map(
                          ([key, value]) => {
                            // Format the key for display
                            const displayKey = key
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase())
                              .trim();

                            return (
                              <div
                                key={key}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "0.5rem 0",
                                  borderBottom: "1px solid #e9ecef",
                                }}
                              >
                                <span
                                  style={{
                                    marginRight: "0.5rem",
                                    fontSize: "1rem",
                                  }}
                                >
                                  {value ? "✅" : "❌"}
                                </span>
                                <span
                                  style={{
                                    color: value ? "#28a745" : "#6c757d",
                                    fontWeight: value ? "500" : "normal",
                                  }}
                                >
                                  {displayKey}
                                </span>
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}

          <History
            entityName="CertificateApplications"
            recordId={application?.id}
            isOpen={showHistory}
            onClose={() => setShowHistory(false)}
          />

          <QueryRemarks
            entityName="CertificateApplications"
            recordId={application?.id}
            isOpen={showQueryRemarks}
            onClose={() => setShowQueryRemarks(false)}
          />

          <QueryApplicationForm
            isOpen={showQueryForm}
            onClose={() => setShowQueryForm(false)}
            entityName="CertificateApplications"
            recordId={application?.id}
            workflowId={application?.workflowId}
            statusId={application?.statusId}
            onSubmit={handleSubmitQuery}
          />

          {/* Edit Application Modal */}
          {showEditModal && application && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div className="modal-content modal-content--side-panel">
                <div className="modal-header">
                  <h2 className="modal-title">Edit Divorce Application</h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => {
                      setShowEditModal(false);
                      resetEditForm();
                    }}
                    disabled={editLoading}
                  >
                    ✕
                  </button>
                </div>

                <form className="modal-form" onSubmit={handleUpdate}>
                  {editError && (
                    <div className="alert alert-error">
                      <span className="alert-icon">⚠️</span>
                      <span>{editError}</span>
                      <button
                        type="button"
                        onClick={() => setEditError("")}
                        className="alert-close"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  {/* Form No Field - Full Width */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: "1rem",
                      width: "100%",
                      marginBottom: "1rem",
                    }}
                  >
                    <div className="form-group has-value">
                      <input
                        type="text"
                        className="form-input"
                        value={application.formNo || ""}
                        disabled
                        placeholder=" "
                        style={{
                          background: "#f1f1f1",
                          cursor: "not-allowed",
                        }}
                      />
                      <label className="form-label">Form No</label>
                    </div>
                  </div>

                  <div className="form-grid-responsive">
                    <div className="switch-container">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={editFormData.processAtHeadquarters || false}
                          disabled={isRoleCountyLinked}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              processAtHeadquarters: e.target.checked,
                              countyId: e.target.checked
                                ? CountyEnum.CentralOffice
                                : linkedCountyId || 0,
                            })
                          }
                        />
                        <span className="switch-slider"></span>
                      </label>
                      <span className="switch-label">
                        Process at Headquarters
                      </span>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={editFormData.countyId || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            countyId: parseInt(e.target.value, 10),
                          })
                        }
                        required
                        disabled={
                          editFormData.processAtHeadquarters ||
                          isRoleCountyLinked
                        }
                        style={
                          editFormData.processAtHeadquarters ||
                          isRoleCountyLinked
                            ? { background: "#f1f1f1", cursor: "not-allowed" }
                            : {}
                        }
                      >
                        <option value="">Select County</option>
                        {editFormData.processAtHeadquarters
                          ? counties
                              .filter(
                                (county) =>
                                  county.id === CountyEnum.CentralOffice,
                              )
                              .map((county) => (
                                <option key={county.id} value={county.id}>
                                  {county.name}
                                </option>
                              ))
                          : counties.map((county) => (
                              <option key={county.id} value={county.id}>
                                {county.name}
                              </option>
                            ))}
                      </select>
                      <label className="form-label">
                        County <span className="required">*</span>
                      </label>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={editFormData.category || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            category: e.target.value,
                          })
                        }
                        disabled
                        style={{
                          background: "#f1f1f1",
                          cursor: "not-allowed",
                        }}
                      >
                        <option value={ApplicationCategory.Marriage}>
                          {ApplicationCategory.Marriage}
                        </option>
                        <option value={ApplicationCategory.Divorce}>
                          {ApplicationCategory.Divorce}
                        </option>
                      </select>
                      <label className="form-label">
                        Application Category <span className="required">*</span>
                      </label>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={editFormData.workflowId || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            workflowId: e.target.value
                              ? parseInt(e.target.value, 10)
                              : undefined,
                          })
                        }
                        disabled
                        style={{
                          background: "#f1f1f1",
                          cursor: "not-allowed",
                        }}
                      >
                        {workflows.map((workflow) => (
                          <option key={workflow.id} value={workflow.id}>
                            {workflow.name}
                          </option>
                        ))}
                      </select>
                      <label className="form-label">Workflow</label>
                    </div>
                  </div>

                  {/* 3-Column Row for Payment Fields */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "1rem",
                      width: "100%",
                      marginBottom: "1rem",
                    }}
                  >
                    {/* Readonly Currency field */}
                    <div
                      className={`form-group ${editServiceCharge?.currencyName ? "has-value" : ""}`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={editServiceCharge?.currencyName ?? ""}
                        readOnly
                        placeholder=" "
                        style={{ background: "#f1f1f1", cursor: "not-allowed" }}
                      />
                      <label className="form-label">Currency</label>
                    </div>

                    {/* Readonly LRA Payment field */}
                    {editServiceCharge?.lraPayment != null && (
                      <div
                        className={`form-group ${editServiceCharge?.lraPayment != null ? "has-value" : ""}`}
                      >
                        <input
                          type="number"
                          className="form-input"
                          value={editServiceCharge?.lraPayment ?? ""}
                          readOnly
                          placeholder=" "
                          style={{
                            background: "#f1f1f1",
                            cursor: "not-allowed",
                          }}
                        />
                        <label className="form-label">LRA Payment</label>
                      </div>
                    )}

                    {/* Readonly Operational Cost field */}
                    {editServiceCharge?.operationalCost != null && (
                      <div
                        className={`form-group ${editServiceCharge?.operationalCost != null ? "has-value" : ""}`}
                      >
                        <input
                          type="number"
                          className="form-input"
                          value={editServiceCharge?.operationalCost ?? ""}
                          readOnly
                          placeholder=" "
                          style={{
                            background: "#f1f1f1",
                            cursor: "not-allowed",
                          }}
                        />
                        <label className="form-label">Operational Cost</label>
                      </div>
                    )}
                  </div>

                  {/* 2-Column Row for Date of Marriage and Email */}
                  <div className="form-grid-responsive">
                    <div className="form-group has-value">
                      <input
                        type="date"
                        className="form-input"
                        value={editFormData.dateOfWedding || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            dateOfWedding: e.target.value,
                          })
                        }
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Date of Marriage <span className="required">*</span>
                      </label>
                    </div>

                    <div
                      className={`form-group ${
                        editFormData.email ? "has-value" : ""
                      }`}
                    >
                      <input
                        type="email"
                        className="form-input"
                        value={editFormData.email || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            email: e.target.value,
                          })
                        }
                        placeholder=" "
                      />
                      <label className="form-label">Email</label>
                    </div>
                  </div>

                  {/* Place of Divorce Field - Full Width */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: "1rem",
                      width: "100%",
                      marginBottom: "1rem",
                    }}
                  >
                    <div
                      className={`form-group ${
                        editFormData.placeOfWedding ? "has-value" : ""
                      }`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={editFormData.placeOfWedding || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            placeOfWedding: e.target.value,
                          })
                        }
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Place of Divorce <span className="required">*</span>
                      </label>
                    </div>
                  </div>

                  {/* Date of Certification - Optional field */}
                  <div className="form-grid-responsive">
                    <div className="form-group has-value">
                      <input
                        type="date"
                        className="form-input"
                        value={editFormData.dateOfCertification || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            dateOfCertification: e.target.value,
                          })
                        }
                        placeholder=" "
                      />
                      <label className="form-label">
                        Date of Certification
                      </label>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={() => {
                        setShowEditModal(false);
                        resetEditForm();
                      }}
                      disabled={editLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={editLoading}
                    >
                      {editLoading ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          Updating...
                        </>
                      ) : (
                        "Update Application"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Manual Payment Modal */}
          {showManualPaymentModal && application && (
            <ManualPayment
              application={application}
              onClose={() => setShowManualPaymentModal(false)}
              onPaymentSuccess={() => fetchData()}
            />
          )}

          {/* Signature Extraction Modal */}
          {showSignatureExtractionModal && application && signedCertificate && (
            <SignatureExtraction
              application={application}
              documentPath={signedCertificate.documentPath}
              onClose={() => setShowSignatureExtractionModal(false)}
            />
          )}

          {/* Card Payment Modal */}
          {showCardPaymentModal && application && (
            <CardPayment
              application={application}
              onClose={() => setShowCardPaymentModal(false)}
              onPaymentSuccess={() => fetchData()}
            />
          )}
        </div>
      </main>
      {!isPrintPage && <Footer />}
      <style>{`
        @media (min-width: 881px) {
          .lawyer-address-field {
            grid-column: 1 / -1;
          }
        }

        @media print {
          .no-print {
            display: none !important;
          }
          .dashboard-body {
            margin: 0 !important;
            padding: 0 !important;
          }
          .main-content {
            margin: 0 !important;
            padding: 0 !important;
          }
          .user-detail-page {
            padding: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DivorceApplicationDetail;
